import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faVideoCamera,
  faComments,
  faFileText,
  faAreaChart,
  faPause,
  faPlay,
  faTimes,
  faPencilAlt,
  faEnvelope,
  faMapMarkerAlt,
  faClock,
  faHeart,
  faLaptopMedical,
  faBullseye,
  faUserMd,
  faUsers,
  faPlus,
  faTrash,
  faSpinner,
  faCheckCircle,
  faList,
} from '@fortawesome/free-solid-svg-icons';
import patientService from '../../../services/patientService';
import practiceService from '../../../services/practiceService';
import ccmService from '../../../services/ccmService';
import socketService from '../../../services/socketService';
import ringcentralService from '../../../services/ringcentralService';
import {
  disposeWebPhone,
  hangupActiveCall,
  hasActiveCall,
  placeOutboundCall,
  requestMicrophone,
  startWebPhone,
  toWebPhoneDigits,
  toWebPhoneCallee,
  describeMicError,
} from '../../../utils/rcWebPhone';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { buildPracticePatientTabPath, isFromPracticeDashboard } from '../../../utils/practiceNavigation';
import { toast } from 'react-toastify';
import BackButton from '../../../components/BackButton/BackButton';
import AppDateInput from '../../../components/Common/AppDateInput/AppDateInput';
import FormDetailSkeleton from '../../../components/SkeletonLoader/FormDetailSkeleton';
import ScreenSectionsSkeleton from '../../../components/SkeletonLoader/ScreenSectionsSkeleton';
import AddFollowUpModal from '../../../components/AddFollowUpModal/AddFollowUpModal';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import ScheduleTargetSettings from '../ScheduleTargetSettings/ScheduleTargetSettings';
import PracticeInlineEventLogs from '../PracticeDashboard/PracticeInlineEventLogs';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import inventoryService from '../../../services/inventoryService';
import {
  METER_EXTENSION_OPTIONS,
  normalizeDeviceSerialInput,
  getDeviceSerialNumberError,
  formatMeterExtensionLabel,
  normalizeMeterExtensionValue,
  formatMeterProductOptionLabel,
  resolveMeterProductDisplayLabel,
} from '../../../utils/meterMappingUtils';
import { checkBPValues, checkBGValue, checkWeightValue, checkPulseValue, formatMeasurementTableDateTime, getBmiCategoryStatus } from '../../../utils/measurementUtils';
import { getPracticeMonitoringSectionFlags } from '../../../utils/practiceMonitoringUtils';
import {
  getBloodPressureTargetForMeasurement,
  getBloodGlucoseTargetForMeasurement,
  resolveEffectiveScheduleTargets,
  hasAnyScheduleTargets
} from '../../../utils/scheduleTargetUtils';
import { PATIENT_SCHEDULE_TARGET_PREF_EVENT } from '../../../utils/patientScheduleTargetPreference.js';
import { buildPatientProfileImageUrl } from '../../../utils/patientProfileImage.js';
import pulseIcon from '../../../assets/pulse.png';
import PulseIcon from '../../../components/Common/PulseIcon/PulseIcon';
import CarePlanPanel from './CCMCarePlanPanel';
import PatientPcmProgramPane from './PcmCarePlan/PatientPcmProgramPane';
import CcmComplexityAttestationPanel from './CcmComplexityAttestationPanel';
import PatientDocumentsSection from '../../../components/PatientDocuments/PatientDocumentsSection';
import PatientDemographicsBanner from './PatientDemographicsBanner';
import PatientRpmOverview from './PatientRpmOverview';
import RpmCarePlanTab from './RpmCarePlan/RpmCarePlanTab';
import { getLatestVitalsSummary } from './RpmCarePlan/rpmCarePlanUtils';
import { AddPatientModal } from './AddPatient/AddPatientModal';
import { parsePatientVitals } from '../../../components/MeasurementData/VitalsTabs/VitalsTabs';
import EventLogSection from '../../../components/EventLogSection/EventLogSection';
import useEventLogModalScrollLock from '../../../hooks/useEventLogModalScrollLock';
import { ROLES, getRoleId } from '../../../utils/roleUtils';
import { toE164 } from '../../../utils/phoneUtils';
import { normalizePatientChronicConditions } from '../../../utils/patientChronicConditionUtils';
import {
  getUniqueChronicConditions,
  DEFAULT_PROGRAM_TIMERS,
  getPatientTimerStorageKey,
  loadPersistedTimers,
  persistTimers,
  bindTimerPersistOnPageHide,
  clearPersistedTimers,
  finalizePatientSessionTimers,
  applyExclusiveProgramTimerState,
  getRpm99458ChipDetail
} from '../../../utils/patientBillingUtils';
import { formatFollowUpNoteFooter } from '../../../utils/followUpDisplayUtils';
import { getBillingPeriodFromStartDate, getCalendarMonthPeriod } from '../../../utils/billingPeriodUtils';
import './PatientDetails.css';
import './PatientProfileLayout.css';

const RC_PENDING_CALL_KEY = 'evitals.ringcentral.pendingCall';

const DEFAULT_TARGETS = {
  bloodPressureTargetRange: [{
    systolicMin: 100,
    systolicMax: 140,
    diastolicMin: 60,
    diastolicMax: 90,
    pulseMin: 60,
    pulseMax: 100
  }],
  bloodGlucoseTargetRange: [{
    minimum: 60,
    maximum: 110
  }],
  weightTargetRange: {
    weightMin: 66,
    weightMax: 220,
    bodyFatMin: 14,
    bodyFatMax: 27,
    bmiNormal: 18.5,
    bmiOverweight: 25,
    bmiObese: 30
  }
};

const CPT_CODE_DETAILS = {
  '99453': {
    title: 'CPT Code 99453',
    description:
      'Covers initial remote patient monitoring device setup, connection training, and patient education.',
    status: 'Status: Setup / education compliance',
  },
  '99445': {
    title: 'CPT Code 99445',
    description:
      'Covers device supply and automated data transmission for 2 to 15 days in a 30-day period. Cannot be billed alongside 99454.',
    status: 'Status: Transmission-day compliance (2–15 days)',
  },
  '99454': {
    title: 'CPT Code 99454',
    description:
      'Requires a minimum of 16 daily device transmissions within a 30-day calendar period. Cannot be billed alongside 99445.',
    status: 'Status: Transmission-day compliance',
  },
  '99470': {
    title: 'CPT Code 99470',
    description:
      'Treatment management services by clinical staff or a physician for 10 to 19 minutes in a calendar month with at least one interactive communication. Cannot be billed together with 99457.',
    status: 'Status: Treatment management minutes (10–19)',
  },
  '99457': {
    title: 'CPT Code 99457',
    description:
      'Remote physiologic monitoring treatment management services. Requires 20 minutes of interactive clinical time in a billing period. Cannot be billed together with 99470.',
    status: 'Status: Treatment management minutes',
  },
  '99458': {
    title: 'CPT Code 99458',
    description:
      'Add-on code for each additional 20 minutes of RPM treatment management services in the same period.',
    status: 'Status: Additional treatment minutes',
  },
  '99091': {
    title: 'CPT Code 99091',
    description:
      'Collection and interpretation of physiologic data digitally stored and/or transmitted by the patient.',
    status: 'Status: Data interpretation minutes',
  },
  '99490': {
    title: 'CPT Code 99490',
    description:
      'Chronic care management services, at least 20 minutes of clinical staff time directed by a physician or other qualified professional.',
    status: 'Status: CCM clinical staff minutes',
  },
  '99439': {
    title: 'CPT Code 99439',
    description:
      'CCM add-on for each additional 20 minutes of clinical staff time in the same calendar month.',
    status: 'Status: Additional CCM staff minutes',
  },
  '99491': {
    title: 'CPT Code 99491',
    description:
      'Chronic care management services provided personally by a physician or other qualified health care professional, at least 30 minutes.',
    status: 'Status: Provider CCM minutes',
  },
  '99437': {
    title: 'CPT Code 99437',
    description:
      'CCM add-on for each additional 30 minutes of physician or QHP time in the same calendar month.',
    status: 'Status: Additional provider CCM minutes',
  },
  '99487': {
    title: 'CPT Code 99487',
    description:
      'Complex chronic care management services requiring moderate or high complexity medical decision making, at least 60 minutes.',
    status: 'Status: Complex CCM minutes',
  },
  '99489': {
    title: 'CPT Code 99489',
    description:
      'Complex CCM add-on for each additional 30 minutes of clinical staff time in the same calendar month.',
    status: 'Status: Additional complex CCM minutes',
  },
  '99424': {
    title: 'CPT Code 99424',
    description:
      'Principal care management by a physician or QHP for a single high-risk chronic condition, first 30 minutes per calendar month.',
    status: 'Status: PCM physician first 30 minutes',
  },
  '99425': {
    title: 'CPT Code 99425',
    description:
      'PCM physician/QHP add-on for each additional 30 minutes in the same calendar month.',
    status: 'Status: Additional PCM physician minutes',
  },
  '99426': {
    title: 'CPT Code 99426',
    description:
      'Principal care management by clinical staff directed by a physician or QHP, first 30 minutes per calendar month.',
    status: 'Status: PCM clinical staff first 30 minutes',
  },
  '99427': {
    title: 'CPT Code 99427',
    description:
      'PCM clinical staff add-on for each additional 30 minutes in the same calendar month.',
    status: 'Status: Additional PCM staff minutes',
  },
};

/** Wireframe-style CPT chips (RPM + CCM) — fallbacks until billing_compliance loads */
const SESSION_TIMER_PILLS_FALLBACK = {
  RPM: [
    { code: '99453', variant: 'pending', badge: '—', showCheck: false },
    { code: '99445', variant: 'pending', badge: '0 of 15d', showCheck: false },
    { code: '99454', variant: 'pending', badge: '0 of 16d', showCheck: false },
    { code: '99470', variant: 'pending', badge: '0 of 19m', showCheck: false },
    { code: '99457', variant: 'pending', badge: '0 of 20m', showCheck: false },
    { code: '99458', variant: 'pending', badge: null, showCheck: false, show99458Detail: false },
    // { code: '99091', variant: 'pending', badge: '0 of 30m', showCheck: false }, // disabled for now
  ],
  CCM: [
    { code: '99490', variant: 'pending', badge: '0 of 20m', showCheck: false },
    { code: '99439', variant: 'pending', badge: '0 of 40m', showCheck: false },
    { code: '99491', variant: 'pending', badge: '0 of 30m', showCheck: false },
    { code: '99437', variant: 'pending', badge: '0 of 60m', showCheck: false },
    { code: '99487', variant: 'pending', badge: '0 of 60m', showCheck: false },
    { code: '99489', variant: 'pending', badge: '0 of 30m', showCheck: false },
  ],
  PCM: [
    { code: '99424', variant: 'pending', badge: '0 of 30m', showCheck: false },
    { code: '99425', variant: 'pending', badge: '0 of 60m', showCheck: false },
    { code: '99426', variant: 'pending', badge: '0 of 30m', showCheck: false },
    { code: '99427', variant: 'pending', badge: '0 of 60m', showCheck: false },
  ],
};

const RPM_PROFILE_CPT_ORDER = ['99453', '99454', '99457', '99445', '99470', '99458'];

const buildRpmProfileFallbackPill = (code, billingCompliance = {}) => {
  const days = Math.max(0, Number(billingCompliance.measurement_days) || 0);
  const minutes = Math.max(0, Number(billingCompliance.service_time_minutes) || 0);
  if (code === '99445') {
    return { code, variant: days >= 2 && days <= 15 ? 'success' : 'pending', badge: `${Math.min(days, 15)} of 15 d` };
  }
  if (code === '99454') {
    return { code, variant: days >= 16 ? 'success' : 'pending', badge: `${Math.min(days, 16)} of 16 d` };
  }
  if (code === '99470') {
    return { code, variant: minutes >= 10 && minutes < 20 ? 'success' : 'pending', badge: `${Math.min(minutes, 19)} of 19 m` };
  }
  if (code === '99457') {
    return { code, variant: minutes >= 20 ? 'success' : 'pending', badge: `${Math.min(minutes, 20)} of 20 m` };
  }
  if (code === '99458') {
    const detail = getRpm99458ChipDetail(minutes);
    return {
      code,
      variant: detail.units > 0 ? 'success' : 'pending',
      badge: `${detail.tierProgress} of ${detail.target} m`,
      show99458Detail: true,
      addonUnits: detail.units,
      addonTierProgress: detail.tierProgress,
      addonTierTarget: detail.target
    };
  }
  return { code, variant: 'pending', badge: '—', showCheck: false };
};

const PROFILE_PROGRAMS = ['RPM', 'CCM', 'PCM'];
const isProfileProgram = (value) => PROFILE_PROGRAMS.includes(String(value || '').toUpperCase());

const mapBillingPillToChip = (pill, { serviceTimeMinutes } = {}) => {
  if (!pill?.code) return null;
  const variantRaw = String(pill.variant || 'pending').toLowerCase();
  const variant =
    variantRaw === 'success' || variantRaw === 'info' || variantRaw === 'warning'
      ? variantRaw
      : variantRaw === 'new-alt' || variantRaw === 'neutral'
        ? 'neutral'
        : 'pending';
  const showCheck = Boolean(pill.icon) || variant === 'success';

  let show99458Detail = pill.show99458Detail;
  let addonUnits = pill.addonUnits != null ? Number(pill.addonUnits) : null;
  let addonTierProgress = pill.addonTierProgress != null ? Number(pill.addonTierProgress) : null;
  let addonTierTarget = pill.addonTierTarget != null ? Number(pill.addonTierTarget) : 20;

  if (pill.code === '99458') {
    const detail = getRpm99458ChipDetail(serviceTimeMinutes);
    if (show99458Detail == null) {
      show99458Detail = detail.show;
    }
    if (addonUnits == null) addonUnits = detail.units;
    if (addonTierProgress == null) addonTierProgress = detail.tierProgress;
    addonTierTarget = detail.target;
  }

  const badge = pill.status
    ? String(pill.status).replace(/\n/g, '/').replace(/\s*\/\s*/g, '/').trim()
    : null;
  return {
    code: String(pill.code),
    variant: showCheck ? 'success' : variant,
    badge: pill.code === '99458' && !show99458Detail ? null : badge,
    showCheck,
    show99458Detail: Boolean(show99458Detail),
    addonUnits: addonUnits ?? 0,
    addonTierProgress: addonTierProgress ?? 0,
    addonTierTarget
  };
};

/** UI only: one 99458 chip per earned/in-progress 20m tier (billing caps at 2 units / 40 & 60 min). */
const MAX_RPM_99458_BILLING_UNITS = 2;

const expand99458PillsForUi = (pills) => pills.flatMap((pill) => {
  if (pill.code !== '99458') return [pill];
  if (!pill.show99458Detail) {
    return [{ ...pill, showCheck: false }];
  }

  const units = Math.min(
    MAX_RPM_99458_BILLING_UNITS,
    Math.max(0, Number(pill.addonUnits) || 0)
  );
  const tier = Math.max(0, Number(pill.addonTierProgress) || 0);
  const target = pill.addonTierTarget || 20;
  const chips = [];

  for (let i = 0; i < units; i += 1) {
    chips.push({
      ...pill,
      chipKey: `99458-complete-${i}`,
      showCheck: false,
      variant: 'success',
      addonUnits: 0,
      addonTierProgress: target,
      addonTierTarget: target
    });
  }

  const canShowAnotherTier = units < MAX_RPM_99458_BILLING_UNITS;
  const needsInProgress = canShowAnotherTier && (units === 0 || tier < target);
  if (needsInProgress) {
    chips.push({
      ...pill,
      chipKey: '99458-progress',
      showCheck: false,
      variant: tier >= target ? 'success' : 'pending',
      addonUnits: 0,
      addonTierProgress: units === 0 ? tier : tier,
      addonTierTarget: target
    });
  }

  return chips.length > 0 ? chips : [{ ...pill, showCheck: false }];
});

const formatDateMMDDYYYY = (value) => {
  if (!value) return '—';
  let date;
  if (value instanceof Date) {
    date = value;
  } else {
    const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      date = new Date(
        Number.parseInt(match[1], 10),
        Number.parseInt(match[2], 10) - 1,
        Number.parseInt(match[3], 10)
      );
    } else {
      date = new Date(value);
    }
  }
  if (Number.isNaN(date.getTime())) return '—';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const SessionCptChip = ({ pill }) => {
  const chipRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const details = CPT_CODE_DETAILS[pill.code] || {
    title: `CPT Code ${pill.code}`,
    description: `Billing compliance details for CPT ${pill.code}.`,
    status: pill.showCheck
      ? 'Status: Achieved'
      : pill.badge
        ? `Status: ${pill.badge}`
        : 'Status: Not achieved',
  };

  const showTooltip = () => {
    const rect = chipRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 260;
    const left = Math.min(
      Math.max(8, rect.left),
      window.innerWidth - width - 8
    );
    setTooltipPos({ top: rect.bottom + 8, left });
  };

  const hideTooltip = () => setTooltipPos(null);

  return (
    <>
      <button
        type="button"
        ref={chipRef}
        className={`pd-cpt-chip pd-cpt-chip--${pill.variant || 'neutral'}${
          pill.code === '99458' && pill.show99458Detail ? ' pd-cpt-chip--99458-detail' : ''
        }`}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        aria-describedby={tooltipPos ? `cpt-tip-${pill.code}` : undefined}
      >
        <span className="pd-cpt-chip-code">{pill.code}</span>
        {pill.showCheck && pill.code !== '99458' ? (
          <FontAwesomeIcon icon={faCheckCircle} className="pd-cpt-chip-check" />
        ) : null}
        {pill.code === '99458' && pill.show99458Detail ? (
          <span className="pd-cpt-chip-badge pd-cpt-chip-badge--99458">
            <span className="pd-cpt-chip-badge-progress">
              {pill.addonTierProgress} of {pill.addonTierTarget}m
            </span>
          </span>
        ) : pill.badge ? (
          <span className="pd-cpt-chip-badge">{pill.badge}</span>
        ) : null}
      </button>
      {tooltipPos
        ? createPortal(
            <div
              id={`cpt-tip-${pill.code}`}
              className="pd-cpt-chip-tooltip is-visible"
              role="tooltip"
              style={{ top: tooltipPos.top, left: tooltipPos.left }}
            >
              <p className="pd-cpt-chip-tooltip-title">{details.title}</p>
              <p className="pd-cpt-chip-tooltip-desc">{details.description}</p>
              <p className="pd-cpt-chip-tooltip-status">
                {pill.code === '99458' && pill.show99458Detail ? (
                  <>
                    99458 add-on tier: {pill.addonTierProgress} of {pill.addonTierTarget} minutes.
                  </>
                ) : pill.showCheck ? (
                  'Status: Achieved'
                ) : (
                  details.status
                )}
              </p>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

const PATIENT_EVENT_LOG_FILTERS = [
  { id: 'all', label: 'All', moduleId: null },
  { id: 'patient', label: 'Patient', moduleId: 'Patient' },
  { id: 'care-plan', label: 'Care Plan', moduleId: 'Patient Care Plan' },
  { id: 'follow-up', label: 'Follow-up', moduleId: 'Patient Follow-up' },
  { id: 'meter', label: 'Meter', moduleId: 'Patient Meter' },
  { id: 'schedule', label: 'Schedule', moduleId: 'Patient Schedule' },
  { id: 'documents', label: 'Documents', moduleId: 'Patient Document' },
  { id: 'vitals', label: 'Vitals', moduleId: 'Patient Vital' },
  { id: 'export', label: 'Export', moduleId: 'Patient Export' },
];

export default function PatientDetails({
  practiceId: practiceIdProp,
  patientId: patientIdProp,
  embeddedInModal = false,
  onReviewStatusChange: onReviewStatusChangeProp,
} = {}) {
  const params = useParams();
  const practiceId = practiceIdProp ?? params.practiceId;
  const patientId = patientIdProp ?? params.patientId;
  const navigate = useNavigate();
  const location = useLocation();
  const requestedProgram = String(
    location.state?.activeProgram || new URLSearchParams(location.search).get('program') || ''
  ).toUpperCase();
  const { updateBreadcrumbs } = useBreadcrumb();
  const { user } = useSelector((state) => state.auth || {});

  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerStorageKey = getPatientTimerStorageKey(practiceId, patientId);
  const [programTimers, setProgramTimers] = useState(() => {
    const loaded = loadPersistedTimers(timerStorageKey);
    return {
      RPM: { seconds: loaded.RPM.seconds, isRunning: loaded.RPM.isRunning },
      CCM: { seconds: loaded.CCM.seconds, isRunning: loaded.CCM.isRunning },
      PCM: { seconds: loaded.PCM?.seconds || 0, isRunning: Boolean(loaded.PCM?.isRunning) }
    };
  });
  const programTimersRef = useRef(programTimers);
  const [activeProgramTab, setActiveProgramTab] = useState(
    isProfileProgram(requestedProgram) ? requestedProgram : 'RPM'
  );
  const [activeRpmSubTab, setActiveRpmSubTab] = useState('Overview');
  const subTabsWrapRef = useRef(null);
  const [subTabIndicatorStyle, setSubTabIndicatorStyle] = useState({ opacity: 0 });
  const [subIndicatorReady, setSubIndicatorReady] = useState(false);

  const updateSubTabIndicator = useCallback(() => {
    const wrap = subTabsWrapRef.current;
    if (!wrap) return;

    const activeButton = wrap.querySelector('.patient-details-sub-tab.active');
    if (!activeButton) {
      setSubTabIndicatorStyle({ opacity: 0 });
      return;
    }

    const wrapRect = wrap.getBoundingClientRect();
    const btnRect = activeButton.getBoundingClientRect();

    setSubTabIndicatorStyle({
      width: `${btnRect.width}px`,
      height: `${btnRect.height}px`,
      transform: `translate(${btnRect.left - wrapRect.left}px, ${btnRect.top - wrapRect.top}px)`,
      opacity: 1,
    });
    setSubIndicatorReady(true);
  }, []);

  useLayoutEffect(() => {
    updateSubTabIndicator();
  }, [activeRpmSubTab, updateSubTabIndicator]);

  useEffect(() => {
    if (activeRpmSubTab === 'Event Logs') {
      setActiveRpmSubTab('Overview');
    }
  }, [activeRpmSubTab]);

  useEffect(() => {
    const wrap = subTabsWrapRef.current;
    if (!wrap) return undefined;

    const resizeObserver = new ResizeObserver(updateSubTabIndicator);
    resizeObserver.observe(wrap);
    window.addEventListener('resize', updateSubTabIndicator);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSubTabIndicator);
    };
  }, [updateSubTabIndicator]);
  const [scheduleEventLogSection, setScheduleEventLogSection] = useState(null);
  const [showScheduleEventLogs, setShowScheduleEventLogs] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [ccmSubTab, setCcmSubTab] = useState('overview');
  const [ccmSavedPlans, setCcmSavedPlans] = useState([]);
  const ccmPlanRef = useRef(null);
  const handleCcmPlansChange = useCallback((nextPlans) => {
    setCcmSavedPlans(Array.isArray(nextPlans) ? nextPlans : []);
  }, []);
  const [ccmBillingRefreshKey, setCcmBillingRefreshKey] = useState(0);
  const [ccmEnrollmentSummary, setCcmEnrollmentSummary] = useState(null);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const [patientToEdit, setPatientToEdit] = useState(null);
  const [editPatientLoading, setEditPatientLoading] = useState(false);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [practiceCaregivers, setPracticeCaregivers] = useState([]);
  const [practiceProviders, setPracticeProviders] = useState([]);

  useEffect(() => {
    if (!practiceId) return;
    let isMounted = true;
    Promise.all([
      practiceService.getPracticeCaregivers(practiceId).catch(() => ({ data: { success: false } })),
      practiceService.getPracticeProviders(practiceId).catch(() => ({ data: { success: false } }))
    ]).then(([cgRes, prRes]) => {
      if (!isMounted) return;
      const cgList = cgRes?.data?.data || cgRes?.data || [];
      const prList = prRes?.data?.data || prRes?.data || [];
      setPracticeCaregivers(Array.isArray(cgList) ? cgList : []);
      setPracticeProviders(Array.isArray(prList) ? prList : []);
    });
    return () => {
      isMounted = false;
    };
  }, [practiceId]);

  const handleAbnormalReviewStatusChange = useCallback((targetPatientId, patch) => {
    if (!patch) return;
    setPatientData((prev) => {
      if (!prev?.patient) return prev;
      return {
        ...prev,
        patient: {
          ...prev.patient,
          ...patch
        }
      };
    });
    if (typeof onReviewStatusChangeProp === 'function') {
      onReviewStatusChangeProp(targetPatientId, patch);
    }
  }, [onReviewStatusChangeProp]);

  const isSuperAdmin = getRoleId(user) === ROLES.SUPER_ADMIN;
  const isPatientSelfView = getRoleId(user) === ROLES.PATIENT;
  const activeProgramTabRef = useRef(activeProgramTab);
  const lastRequestedProgramRef = useRef(requestedProgram);

  useEffect(() => {
    activeProgramTabRef.current = activeProgramTab;
  }, [activeProgramTab]);

  const activeProgramKey = isProfileProgram(activeProgramTab)
    ? String(activeProgramTab).toUpperCase()
    : 'RPM';

  useEffect(() => {
    const carePrograms = patientData?.care_programs || patientData?.patient?.care_programs;
    if (!carePrograms) return;
    const programs = carePrograms.map((program) => (
      typeof program === 'string' ? program.toUpperCase() : String(program.short_name || '').toUpperCase()
    ));
    const urlProgramChanged = isProfileProgram(requestedProgram)
      && requestedProgram !== lastRequestedProgramRef.current;
    lastRequestedProgramRef.current = requestedProgram;
    if (urlProgramChanged && programs.includes(requestedProgram)) {
      setActiveProgramTab(requestedProgram);
      return;
    }
    const currentProgram = activeProgramTabRef.current;
    if (programs.length > 0 && !programs.includes(currentProgram)) {
      const firstSupported = programs.find((program) => isProfileProgram(program));
      setActiveProgramTab(firstSupported || '');
    } else if (programs.length === 0) {
      setActiveProgramTab('');
    }
  }, [patientData, requestedProgram]);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [editingFollowUpNote, setEditingFollowUpNote] = useState(null);
  const [isPhoneCallFollowUp, setIsPhoneCallFollowUp] = useState(false);
  const [phoneCallContext, setPhoneCallContext] = useState(null);
  const [activeRingCentralCallId, setActiveRingCentralCallId] = useState(null);
  const liveCallRef = useRef({ callId: null, startedAt: 0, phase: 'idle' });
  const [followUpPage, setFollowUpPage] = useState(1);
  const [followUpEntries, setFollowUpEntries] = useState('10');
  const followUpWasRunningRef = useRef(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [practiceIsLocked] = useState(false);
  const detailsCacheRef = useRef(new Map());
  const notesCacheRef = useRef(new Map());
  const profileViewLoggedRef = useRef(false);
  const [eventLogModuleFilter, setEventLogModuleFilter] = useState(null);
  const [showEventLogsModal, setShowEventLogsModal] = useState(false);
  const eventLogsOverlayRef = useRef(null);
  const eventLogsContainerRef = useRef(null);
  useEventLogModalScrollLock(showEventLogsModal, eventLogsOverlayRef, eventLogsContainerRef);

  const patientProfilePicUrl = useMemo(
    () => buildPatientProfileImageUrl(patientData?.patient?.profile_pic),
    [patientData?.patient?.profile_pic]
  );

  const sortedFollowUpNotes = useMemo(() => {
    const notes = patientData?.follow_up_notes || [];
    return [...notes].sort((a, b) => {
      const timeA = new Date(a.date || a.created_at || a.createdAt || 0).getTime();
      const timeB = new Date(b.date || b.created_at || b.createdAt || 0).getTime();
      if (timeA !== timeB) return timeB - timeA;
      return (b.id || 0) - (a.id || 0);
    });
  }, [patientData?.follow_up_notes]);

  const paginatedFollowUpNotes = useMemo(() => {
    if (followUpEntries === 'all' || followUpEntries === 'All') return sortedFollowUpNotes;
    const pageSize = parseInt(followUpEntries, 10) || 10;
    const start = (followUpPage - 1) * pageSize;
    return sortedFollowUpNotes.slice(start, start + pageSize);
  }, [sortedFollowUpNotes, followUpPage, followUpEntries]);

  useEffect(() => {
    setFollowUpPage(1);
  }, [activeProgramTab, patientData?.follow_up_notes?.length]);

  // Meter management state
  const [patientMeters, setPatientMeters] = useState([]);
  const [loadingMeters, setLoadingMeters] = useState(false);
  const [showAddMeterModal, setShowAddMeterModal] = useState(false);
  const [addMeterForm, setAddMeterForm] = useState({
    model_number: '',
    extension: '',
    serial_number: '',
    gateway_serial_number: ''
  });

  const [products, setProducts] = useState([]);
  const [showDeleteDeviceModal, setShowDeleteDeviceModal] = useState(false);
  const [showResetTimerModal, setShowResetTimerModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [deletingDevice, setDeletingDevice] = useState(false);
  const [showEditMeterModal, setShowEditMeterModal] = useState(false);
  const [editingMeter, setEditingMeter] = useState(null);
  const [editMeterForm, setEditMeterForm] = useState({
    model_number: '',
    extension: '',
    serial_number: '',
    gateway_serial_number: ''
  });
  const [savingMeterEdit, setSavingMeterEdit] = useState(false);

  // Escape closes Event Logs. Scroll lock for Event Logs is handled only by useEventLogModalScrollLock.
  useEffect(() => {
    if (!showEventLogsModal) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setShowEventLogsModal(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showEventLogsModal]);

  // Lock background scroll while device modals are open (not Event Logs — avoids stuck overflow).
  useEffect(() => {
    const modalOpen = showEditMeterModal || showDeleteDeviceModal || showResetTimerModal;
    if (!modalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow === 'hidden' ? '' : previousOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow === 'hidden' ? '' : previousHtmlOverflow;
    };
  }, [showEditMeterModal, showDeleteDeviceModal, showResetTimerModal]);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await inventoryService.getProductsForDropdown();
      if (res.data && res.data.success) {
        setProducts(res.data.data || []);
      } else if (Array.isArray(res.data)) {
        setProducts(res.data);
      }
    } catch (e) {
      console.error('Failed to load products', e);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const loadCcmEnrollmentSummary = useCallback(async () => {
    const pk = patientData?.patient?.id ?? patientData?.id ?? patientId;
    if (!pk || (activeProgramTab !== 'CCM' && activeProgramTab !== 'PCM')) {
      setCcmEnrollmentSummary(null);
      return;
    }
    try {
      const res = await ccmService.getEnrollmentByPatient(pk, activeProgramTab);
      setCcmEnrollmentSummary(res?.success ? res.data : null);
    } catch {
      setCcmEnrollmentSummary(null);
    }
  }, [patientData, patientId, activeProgramTab]);

  useEffect(() => {
    loadCcmEnrollmentSummary();
  }, [loadCcmEnrollmentSummary, ccmBillingRefreshKey]);

  const loadMeters = useCallback(async () => {
    try {
      setLoadingMeters(true);
      const res = await patientService.getPatientMeters(practiceId, patientId);
      if (res.data && res.data.success) {
        setPatientMeters(res.data.data || []);
      }
    } catch (e) {
      console.error('Failed to load meters', e);
    } finally {
      setLoadingMeters(false);
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    if (activeRpmSubTab === 'Manage meter') {
      loadMeters();
    }
  }, [activeRpmSubTab, loadMeters]);

  const handleAddMeterInline = async () => {
    if (!addMeterForm.model_number || !addMeterForm.extension || !addMeterForm.serial_number) {
      toast.error('Model number, extension, and serial number are required');
      return;
    }
    const serialError = getDeviceSerialNumberError(addMeterForm.serial_number);
    if (serialError) {
      toast.error(serialError);
      return;
    }
    const serialNumber = String(addMeterForm.serial_number || '').trim();
    const duplicateSerial = (patientMeters || []).some(
      (m) => String(m.serial_number || '').trim().toLowerCase() === serialNumber.toLowerCase()
    );
    if (duplicateSerial) {
      toast.error('This serial number is already in the mapped devices list');
      return;
    }
    try {
      const response = await patientService.addMeterToPatient(practiceId, patientId, addMeterForm);
      if (response?.data?.success === false) {
        throw new Error(response.data.message || 'Failed to add meter');
      }
      toast.success('Meter added successfully');
      setAddMeterForm({ model_number: '', extension: '', serial_number: '', gateway_serial_number: '' });
      loadMeters();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to add meter');
    }
  };

  const handleDeleteMeterRow = (m) => {
    setDeviceToDelete(m);
    setShowDeleteDeviceModal(true);
  };

  const handleEditMeterRow = (m) => {
    setEditingMeter(m);
    setEditMeterForm({
      model_number: m.model_number || '',
      extension: normalizeMeterExtensionValue(m.extension) || String(m.extension || '').trim(),
      serial_number: m.serial_number || '',
      gateway_serial_number: m.gateway_serial_number || ''
    });
    setShowEditMeterModal(true);
  };

  const cancelEditMeter = () => {
    if (savingMeterEdit) return;
    setShowEditMeterModal(false);
    setEditingMeter(null);
    setEditMeterForm({
      model_number: '',
      extension: '',
      serial_number: '',
      gateway_serial_number: ''
    });
  };

  const handleSaveEditMeter = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!editingMeter?.id || savingMeterEdit) return;
    if (practiceIsLocked) {
      toast.warning('Practice is locked. Meter changes are not allowed.');
      return;
    }
    if (!editMeterForm.model_number || !editMeterForm.extension || !editMeterForm.serial_number) {
      toast.error('Model number, extension, and serial number are required');
      return;
    }
    const serialError = getDeviceSerialNumberError(editMeterForm.serial_number);
    if (serialError) {
      toast.error(serialError);
      return;
    }
    const serialNumber = String(editMeterForm.serial_number || '').trim();
    const duplicateSerial = (patientMeters || []).some((m) => {
      if (String(m.id) === String(editingMeter.id)) return false;
      return String(m.serial_number || '').trim().toLowerCase() === serialNumber.toLowerCase();
    });
    if (duplicateSerial) {
      toast.error('This serial number is already in the mapped devices list');
      return;
    }
    try {
      setSavingMeterEdit(true);
      const response = await patientService.updateMeterOnPatient(
        practiceId,
        patientId,
        editingMeter.id,
        {
          model_number: editMeterForm.model_number,
          extension: editMeterForm.extension,
          serial_number: serialNumber,
          gateway_serial_number: editMeterForm.gateway_serial_number || ''
        }
      );
      if (response?.data?.success === false) {
        throw new Error(response.data.message || 'Failed to update device');
      }
      toast.success('Device updated successfully');
      if (response?.data?.foracareSync) {
        if (response.data.foracareSync.reCode === '00') {
          toast.success('Device synced with ForaCare');
        } else {
          toast.warning('Device updated but ForaCare sync failed');
        }
      }
      setShowEditMeterModal(false);
      setEditingMeter(null);
      setEditMeterForm({
        model_number: '',
        extension: '',
        serial_number: '',
        gateway_serial_number: ''
      });
      loadMeters();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update device');
    } finally {
      setSavingMeterEdit(false);
    }
  };

  const cancelDeleteDevice = () => {
    if (deletingDevice) return;
    setShowDeleteDeviceModal(false);
    setDeviceToDelete(null);
  };

  const confirmDeleteDevice = async () => {
    if (!deviceToDelete?.id || deletingDevice) return;
    if (practiceIsLocked) {
      toast.warning('Practice is locked. Meter changes are not allowed.');
      return;
    }
    try {
      setDeletingDevice(true);
      const response = await patientService.deleteMeterFromPatient(practiceId, patientId, deviceToDelete.id);
      if (response?.data?.success === false) {
        throw new Error(response.data.message || 'Failed to delete device');
      }
      toast.success('Device deleted successfully');
      if (response?.data?.foracareSync) {
        toast.success('Device removed from ForaCare');
      }
      setShowDeleteDeviceModal(false);
      setDeviceToDelete(null);
      loadMeters();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to delete device');
    } finally {
      setDeletingDevice(false);
    }
  };


  // Appointment form state
  const [appointmentData, setAppointmentData] = useState({
    facility: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    provider: '',
    caregiver: '',
    visitType: '',
    visitStatus: ''
  });

  const fetchPatientNotes = useCallback(async (baseData, careProgram = 'RPM') => {
    const notesPatientId =
      baseData?.patient?.patients_table_id ||
      baseData?.patient?.id ||
      patientId;
    const program = String(careProgram || 'RPM').toUpperCase();
    const notesCacheKey = `${practiceId}:${notesPatientId}:${program}`;
    if (notesCacheRef.current.has(notesCacheKey)) {
      return notesCacheRef.current.get(notesCacheKey) || [];
    }
    const notesResponse = await patientService
      .getPatientNotes(practiceId, notesPatientId, program)
      .catch(() => null);
    const notes = notesResponse?.data?.success ? (notesResponse.data.data || []) : [];
    notesCacheRef.current.set(notesCacheKey, notes);
    return notes;
  }, [practiceId, patientId]);

  const fetchPatientDetails = useCallback(async ({ includeNotes = true, showLoader = true } = {}) => {
    try {
      const detailsCacheKey = `${practiceId}:${patientId}`;
      // Use memoized payload to avoid unnecessary refetches for same patient.
      // Useful in React StrictMode remounts and local re-renders.
      if (includeNotes && detailsCacheRef.current.has(detailsCacheKey)) {
        const cached = detailsCacheRef.current.get(detailsCacheKey);
        setPatientData(cached);
        if (showLoader) setLoading(false);
        return;
      }

      if (showLoader) setLoading(true);
      setError(null);

      const [detailsResponse, programsResponse] = await Promise.all([
        patientService.getPatientDetailsFast(practiceId, patientId, {
          auditView: !profileViewLoggedRef.current
        }),
        patientService.getPatientPrograms(practiceId, patientId).catch(() => ({ data: { success: false } })),
      ]);

      if (detailsResponse.data.success) {
        profileViewLoggedRef.current = true;
        const baseData = detailsResponse.data.data || {};
        if (programsResponse?.data?.success && programsResponse.data.data?.programs) {
          baseData.care_programs = programsResponse.data.data.programs
            .filter(p => p.enrolled)
            .map(p => p.short_name);
        } else {
          baseData.care_programs = [];
        }
        if (includeNotes) {
          const program = String(activeProgramTabRef.current || 'RPM').toUpperCase();
          const followUpNotes = await fetchPatientNotes(baseData, program);
          const next = {
            ...baseData,
            follow_up_notes: followUpNotes
          };
          detailsCacheRef.current.set(detailsCacheKey, next);
          setPatientData(next);
        } else {
          setPatientData((prev) => ({
            ...baseData,
            follow_up_notes: prev?.follow_up_notes || []
          }));
        }
      } else {
        setError(detailsResponse.data.message || 'Failed to load patient details');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load patient details');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [practiceId, patientId, fetchPatientNotes]);

  const refreshBillingCompliance = useCallback(async () => {
    if (!practiceId || !patientId) return;
    try {
      detailsCacheRef.current.delete(`${practiceId}:${patientId}`);
      const detailsResponse = await patientService.getPatientDetailsFast(practiceId, patientId);
      if (!detailsResponse?.data?.success) return;
      const billingCompliance = detailsResponse.data.data?.billing_compliance;
      if (!billingCompliance) return;
      setPatientData((prev) => (prev ? { ...prev, billing_compliance: billingCompliance } : prev));
    } catch (err) {
      console.error('Failed to refresh CPT billing compliance:', err);
    }
  }, [practiceId, patientId]);

  // Clear caches when patient context changes.
  useEffect(() => {
    detailsCacheRef.current.clear();
    notesCacheRef.current.clear();
    profileViewLoggedRef.current = false;
  }, [practiceId, patientId]);

  const fetchScheduleTargets = useCallback(async () => {
    if (!practiceId || !patientId) return;
    try {
      const sources = await patientService.getScheduleTargetSourcesForPatient(practiceId, patientId);
      // Patient profile overview: patient targets win when present, else practice.
      // (Measurement pages may still honor the session practice/patient toggle.)
      const effective = resolveEffectiveScheduleTargets(
        sources.patientData || {},
        sources.practiceData || {}
      );
      setScheduleTargets(hasAnyScheduleTargets(effective) ? effective : sources.practiceData);
    } catch (err) {
      console.error('Error fetching schedule targets for patient details:', err);
      setScheduleTargets(null);
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    fetchScheduleTargets();
  }, [fetchScheduleTargets]);

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    const onPref = (ev) => {
      const d = ev?.detail;
      if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
      fetchScheduleTargets();
    };
    window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
    return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
  }, [practiceId, patientId, fetchScheduleTargets]);

  // Initial / patient-change load only — do not refetch on RPM/CCM tab switch.
  useEffect(() => {
    fetchPatientDetails({ includeNotes: false, showLoader: true });
  }, [practiceId, patientId, fetchPatientDetails]);

  useEffect(() => {
    const handleWindowFocus = () => {
      refreshBillingCompliance();
      fetchScheduleTargets();
    };
    const handleMeasurementAdded = (event) => {
      const evtPracticeId = String(event?.detail?.practiceId || '');
      const evtPatientId = String(event?.detail?.patientId || '');
      if (evtPracticeId !== String(practiceId) || evtPatientId !== String(patientId)) return;
      detailsCacheRef.current.delete(`${practiceId}:${patientId}`);
      fetchPatientDetails({ includeNotes: false, showLoader: false });
      refreshBillingCompliance();
    };
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('weight:measurement-added', handleMeasurementAdded);
    window.addEventListener('bp:measurement-added', handleMeasurementAdded);
    window.addEventListener('bg:measurement-added', handleMeasurementAdded);
    window.addEventListener('measurement:added', handleMeasurementAdded);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('weight:measurement-added', handleMeasurementAdded);
      window.removeEventListener('bp:measurement-added', handleMeasurementAdded);
      window.removeEventListener('bg:measurement-added', handleMeasurementAdded);
      window.removeEventListener('measurement:added', handleMeasurementAdded);
    };
  }, [fetchPatientDetails, fetchScheduleTargets, refreshBillingCompliance, practiceId, patientId]);

  // Socket.IO real-time updates
  useEffect(() => {
    // Connect to Socket.IO
    socketService.connect();

    // Join patient room for real-time updates
    if (practiceId && patientId) {
      socketService.joinPatientRoom(practiceId, patientId);
    }

    // Listen for vital reading updates
    const handleVitalUpdate = (data) => {
      console.log('Real-time vital reading update received:', data);

      // Show toast notification
      const measurementType = data.measurementType || 'measurement';
      const typeLabels = {
        'blood_pressure': 'Blood Pressure',
        'blood_glucose': 'Blood Glucose',
        'weight': 'Weight'
      };
      const label = typeLabels[measurementType] || 'Vital Reading';

      toast.success(`New ${label} reading received!`, {
        position: 'top-right',
        autoClose: 3000
      });

      // Refresh vitals/details + CPT badges without a full page reload
      fetchPatientDetails({ includeNotes: false, showLoader: false });
      refreshBillingCompliance();
    };

    socketService.onVitalReadingUpdate(handleVitalUpdate);

    // Cleanup on unmount
    return () => {
      socketService.offVitalReadingUpdate();
      if (practiceId && patientId) {
        socketService.leavePatientRoom(practiceId, patientId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, patientId]);

  useEffect(() => {
    if (embeddedInModal) return;
    if (patientData && patientData.patient && !loading) {
      const patient = patientData.patient;
      const patientFullName = `${patient.last_name || ''}, ${patient.first_name || ''}`.trim();
      const practiceName = patient.practice_name || 'Practice';

      if (isPatientSelfView) {
        updateBreadcrumbs([
          { label: 'Dashboard', path: '/patient-dashboard', active: true },
        ]);
        return;
      }

      const sourceTrail = Array.isArray(location.state?.breadcrumbTrail)
        ? location.state.breadcrumbTrail
            .filter((crumb) => crumb && typeof crumb.label === 'string')
            .map((crumb) => ({
              label: crumb.label,
              path: typeof crumb.path === 'string' ? crumb.path : null,
              ...(crumb.state && typeof crumb.state === 'object' ? { state: crumb.state } : {})
            }))
        : [];

      if (sourceTrail.length > 0) {
        updateBreadcrumbs([
          ...sourceTrail,
          { label: patientFullName || 'Patient', path: null, active: true }
        ]);
        return;
      }

      const fromDashboard = isFromPracticeDashboard(location);
      const rootLabel = 'Dashboard';
      const rootPath = '/dashboard';
      const dashboardTabSuffix = fromDashboard ? '&fromDashboard=true' : '';
      const activeProgram = location.state?.activeProgram;

      updateBreadcrumbs([
        { label: rootLabel, path: rootPath },
        { label: practiceName, path: `${fromDashboard ? '/dashboard' : '/practices'}/${practiceId}?tab=practice${dashboardTabSuffix}` },
        {
          label: 'Patients',
          path: buildPracticePatientTabPath(practiceId, { fromDashboard, program: activeProgram })
        },
        { label: patientFullName || 'Patient', path: null, active: true }
      ]);
    }

  }, [patientData, practiceId, loading, updateBreadcrumbs, location, isPatientSelfView, embeddedInModal]);

  useEffect(() => {
    programTimersRef.current = programTimers;
  }, [programTimers]);

  // Restore shared session timers when switching patients; keep them across profile <-> measurements.
  useEffect(() => {
    const loaded = loadPersistedTimers(timerStorageKey);
    const next = {
      RPM: { seconds: loaded.RPM.seconds, isRunning: loaded.RPM.isRunning },
      CCM: { seconds: loaded.CCM.seconds, isRunning: loaded.CCM.isRunning },
      PCM: { seconds: loaded.PCM?.seconds || 0, isRunning: Boolean(loaded.PCM?.isRunning) }
    };
    programTimersRef.current = next;
    setProgramTimers(next);

    return () => {
      // Keep timer only when moving to another screen for this same patient
      // (measurements/charts). Leaving to the patients list clears it.
      finalizePatientSessionTimers(
        timerStorageKey,
        {
          ...programTimersRef.current,
          activeProgram: activeProgramTabRef.current
        },
        { practiceId, patientId }
      );
    };
  }, [timerStorageKey, practiceId, patientId]);

  useEffect(() => {
    if (loading) return;
    persistTimers(timerStorageKey, {
      ...programTimers,
      activeProgram: activeProgramTab
    });
  }, [programTimers, timerStorageKey, activeProgramTab, loading]);

  useEffect(() => bindTimerPersistOnPageHide(), []);

  useEffect(() => {
    if (practiceIsLocked) {
      setProgramTimers({
        RPM: { seconds: 0, isRunning: false },
        CCM: { seconds: 0, isRunning: false },
        PCM: { seconds: 0, isRunning: false }
      });
      clearPersistedTimers(timerStorageKey);
      return;
    }

    // Pause timers while patient details are still loading.
    if (loading || !patientData?.patient) {
      setProgramTimers((prev) => ({
        RPM: { ...prev.RPM, isRunning: false },
        CCM: { ...prev.CCM, isRunning: false }
      }));
      return;
    }

    setProgramTimers((prev) => applyExclusiveProgramTimerState(prev, activeProgramTab, { practiceIsLocked }));
    // Only re-apply when patient identity / tab / lock changes — not on every details refresh
  }, [activeProgramTab, practiceIsLocked, loading, patientData?.patient?.id, timerStorageKey]);

  const patientNotesBaseRef = useRef(null);
  useEffect(() => {
    patientNotesBaseRef.current = patientData;
  }, [patientData?.patient?.id, patientData?.patient?.patients_table_id, patientData?.id]);

  useEffect(() => {
    if (loading) return;
    const base = patientNotesBaseRef.current;
    const notesPatientId = base?.patient?.patients_table_id || base?.patient?.id || patientId;
    if (!notesPatientId) return;

    let cancelled = false;
    (async () => {
      try {
        const followUpNotes = await fetchPatientNotes(base || { patient: { id: notesPatientId } }, activeProgramKey);
        if (!cancelled) {
          setPatientData((prev) => (prev ? { ...prev, follow_up_notes: followUpNotes } : prev));
        }
      } catch {
        // keep existing notes
      }
    })();

    return () => { cancelled = true; };
  }, [
    activeProgramKey,
    patientData?.patient?.id,
    patientData?.patient?.patients_table_id,
    loading,
    fetchPatientNotes,
    patientId,
  ]);

  const activeTimer = programTimers[activeProgramKey] || DEFAULT_PROGRAM_TIMERS[activeProgramKey];
  const timer = activeTimer.seconds;
  const isRunning = Boolean(activeTimer.isRunning);

  useEffect(() => {
    let interval = null;
    if (isRunning && !practiceIsLocked && !loading) {
      interval = setInterval(() => {
        setProgramTimers((prev) => ({
          ...prev,
          [activeProgramKey]: {
            ...prev[activeProgramKey],
            seconds: (prev[activeProgramKey]?.seconds || 0) + 1
          }
        }));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, activeProgramKey, practiceIsLocked, loading]);

  const fetchPatientMeters = async () => {
    try {
      setLoadingMeters(true);
      const response = await patientService.getPatientMeters(practiceId, patientId);
      if (response.data.success) {
        setPatientMeters(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching patient meters:', error);
    } finally {
      setLoadingMeters(false);
    }
  };

  const handleAddMeter = async (e) => {
    e.preventDefault();
    if (practiceIsLocked) {
      toast.warning('Practice is locked. Meter changes are not allowed.');
      return;
    }

    // Validation
    if (!addMeterForm.model_number || !addMeterForm.extension || !addMeterForm.serial_number) {
      toast.error('Please fill in all required fields (Model, Extension, Serial Number)');
      return;
    }

    const serialError = getDeviceSerialNumberError(addMeterForm.serial_number);
    if (serialError) {
      toast.error(serialError);
      return;
    }

    const serialNumber = String(addMeterForm.serial_number || '').trim();
    const metersList = patientMeters?.length ? patientMeters : [];
    const duplicateSerial = metersList.some(
      (m) => String(m.serial_number || '').trim().toLowerCase() === serialNumber.toLowerCase()
    );
    if (duplicateSerial) {
      toast.error('This serial number is already in the mapped devices list');
      return;
    }

    try {
      const response = await patientService.addMeterToPatient(practiceId, patientId, addMeterForm);
      if (response.data.success) {
        toast.success('Meter added successfully');
        setShowAddMeterModal(false);
        setAddMeterForm({
          model_number: '',
          extension: '',
          serial_number: '',
          gateway_serial_number: ''
        });
        fetchPatientMeters(); // Refresh list
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add meter');
      console.error('Error adding meter:', error);
    }
  };

  const handleDeleteMeter = (meterId) => {
    if (practiceIsLocked) {
      toast.warning('Practice is locked. Meter changes are not allowed.');
      return;
    }
    setDeviceToDelete({ id: meterId });
    setShowDeleteDeviceModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]}, ${date.getDate().toString().padStart(2, '0')} ${date.getFullYear()}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const raw = String(dateString).trim();

    // Preserve stored wall-clock time from DB strings like "YYYY-MM-DD HH:mm:ss"
    // and avoid browser timezone shifting that can alter displayed time.
    const localLike = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (localLike) {
      const year = Number(localLike[1]);
      const month = localLike[2];
      const day = localLike[3];
      const hours = Number(localLike[4]);
      const minutes = localLike[5];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${month}-${day}-${year}, ${displayHours}:${minutes} ${ampm}`;
    }

    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${month}-${day}-${year}, ${displayHours}:${minutes} ${ampm}`;
  };

  // Prefer measurement_date + measurement_time (same fields as Measure Time on measurement pages).
  // Do NOT prefer measure_date_time / measure_new_date_time first — those often arrive as ISO
  // UTC strings and get wall-clock-parsed into the wrong hour (e.g. 4:44 PM -> 8:44 PM).
  const getVitalDisplayDateTime = (reading) => {
    // Shared wall-clock formatter (no browser TZ shift) — same as measurement pages.
    return formatMeasurementTableDateTime(reading);
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    // Parse YYYY-MM-DD directly to avoid UTC->local timezone shift
    const match = String(dateString).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[2]}-${match[3]}-${match[1]}`;
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const formatDateMDY = (dateValue) => {
    if (!dateValue) return '-';
    // Parse YYYY-MM-DD directly to avoid UTC->local timezone shift
    const match = String(dateValue).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${Number(match[2])}-${Number(match[3])}-${match[1]}`;
    }
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '-';
    return `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
  };

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const updateActiveProgramTimer = (patch) => {
    setProgramTimers((prev) => ({
      ...prev,
      [activeProgramKey]: {
        ...DEFAULT_PROGRAM_TIMERS[activeProgramKey],
        ...prev[activeProgramKey],
        ...patch
      }
    }));
  };

  const startTimer = () => {
    updateActiveProgramTimer({ isRunning: true });
  };

  const pauseTimer = () => {
    updateActiveProgramTimer({ isRunning: false });
  };

  const openFollowUpModal = () => {
    setEditingFollowUpNote(null);
    setIsPhoneCallFollowUp(false);
    setPhoneCallContext(null);
    setActiveRingCentralCallId(null);
    followUpWasRunningRef.current = Boolean(
      (programTimers[activeProgramKey] || DEFAULT_PROGRAM_TIMERS[activeProgramKey])?.isRunning
    );
    // Pause parent session timer while modal owns the clock
    updateActiveProgramTimer({ isRunning: false });
    setShowFollowUpModal(true);
  };

  const openPhoneCallFollowUp = (context = null) => {
    setEditingFollowUpNote(null);
    setIsPhoneCallFollowUp(true);
    setPhoneCallContext(context);
    followUpWasRunningRef.current = Boolean(
      (programTimers[activeProgramKey] || DEFAULT_PROGRAM_TIMERS[activeProgramKey])?.isRunning
    );
    updateActiveProgramTimer({ isRunning: false });
    setShowFollowUpModal(true);
  };

  const resolvePatientDialNumber = () => {
    const record = patientData?.patient || {};
    return (
      record.cell_phone_number ||
      record.phone_number ||
      record.cellPhone ||
      record.homePhone ||
      null
    );
  };

  const patchLiveCall = async (callId, payload) => {
    if (!callId) return;
    try {
      await ringcentralService.updateCall(callId, payload);
    } catch {
      // Keep the in-browser call usable if status persist fails.
    }
  };

  const hangupLiveRingcentralCall = async ({ failedMessage } = {}) => {
    const callId = liveCallRef.current.callId || activeRingCentralCallId;
    const startedAt = liveCallRef.current.startedAt || Date.now();
    const durationSeconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
    liveCallRef.current.phase = 'done';
    try {
      await hangupActiveCall();
    } catch {
      // Already ended.
    }
    if (callId) {
      await patchLiveCall(callId, {
        status: failedMessage ? 'failed' : 'ended',
        durationSeconds,
        errorMessage: failedMessage || undefined,
      });
    }
    setPhoneCallContext((prev) => ({
      ...(prev || {}),
      status: failedMessage ? 'failed' : 'ended',
      statusMessage: failedMessage
        || 'Call ended. Fetching duration, recording, and RingSense summary...',
      durationSeconds,
    }));
  };

  const startRingcentralConnect = (toNumber) => {
    sessionStorage.setItem(RC_PENDING_CALL_KEY, JSON.stringify({
      practiceId,
      patientId,
      toNumber: toNumber || null,
    }));
    window.location.href = ringcentralService.getOAuthStartUrl(
      `${location.pathname}${location.hash || ''}`
    );
  };

  const placePatientRingcentralCall = async ({ toNumber, skipConnect = false } = {}) => {
    const mic = await requestMicrophone();
    if (!mic.ok) {
      toast.error(mic.message);
      openPhoneCallFollowUp({
        status: 'failed',
        statusMessage: mic.message,
        summary: null,
        durationSeconds: null,
      });
      return;
    }

    liveCallRef.current = { callId: null, startedAt: Date.now(), phase: 'connecting' };
    openPhoneCallFollowUp({
      status: 'connecting',
      statusMessage: 'Connecting... preparing the browser call.',
      summary: null,
      durationSeconds: null,
    });

    try {
      const webphoneRes = await ringcentralService.getWebPhone();
      const sipInfo = webphoneRes?.data?.sipInfo;
      const fromNumber = webphoneRes?.data?.fromNumber;
      if (!sipInfo) {
        throw new Error('RingCentral did not return browser-call credentials.');
      }

      const response = await ringcentralService.placeCall(practiceId, patientId, {
        toNumber: toNumber || undefined,
      });
      const call = response?.data;
      if (call?.id) {
        setActiveRingCentralCallId(call.id);
        liveCallRef.current.callId = call.id;
      }

      await startWebPhone(sipInfo);
      const callee = toWebPhoneCallee(call?.toNumber || toNumber);
      const callerId = toWebPhoneDigits(fromNumber || call?.fromNumber);
      if (!callee) {
        throw new Error('No valid patient number to dial.');
      }

      liveCallRef.current.phase = 'calling';
      setPhoneCallContext((prev) => ({
        ...(prev || {}),
        status: 'calling',
        toNumber: call?.toNumber || prev?.toNumber || null,
        fromNumber: call?.fromNumber || fromNumber || prev?.fromNumber || null,
        statusMessage: call?.toNumber
          ? `Calling the patient's phone ${call.toNumber}. Talk through this browser when they answer. Your RingCentral app will not ring.`
          : 'Calling the patient. Talk through this browser when they answer.',
      }));
      await patchLiveCall(call?.id, { status: 'calling' });
      if (call?.toNumber) {
        toast.info(`Calling patient at ${call.toNumber}`);
      }

      placeOutboundCall(callee, callerId, {
        onCalling: (session) => {
          const sessionId = session?.sessionId;
          if (sessionId) {
            patchLiveCall(call?.id, { status: 'calling', telephonySessionId: sessionId });
          }
          setPhoneCallContext((prev) => ({
            ...(prev || {}),
            status: 'calling',
            toNumber: call?.toNumber || prev?.toNumber || null,
            statusMessage: call?.toNumber
              ? `Calling the patient's phone ${call.toNumber}. Talk through this browser when they answer. Your RingCentral app will not ring.`
              : 'Calling the patient. Talk through this browser when they answer.',
          }));
        },
        onAnswered: (session) => {
          // RingCentral often sends an immediate SIP 200 OK; Call Control poll confirms Connected.
          const sessionId = session?.sessionId;
          if (sessionId) {
            patchLiveCall(call?.id, { telephonySessionId: sessionId });
          }
        },
        onFailed: (message) => {
          // RingCentral SIP can report Wrong Number while the patient phone is still ringing.
          console.warn('RingCentral WebPhone failed event:', message);
        },
        onEnded: () => {
          // Do not end the E-Vitals UI here. PSTN ringing can continue after SIP disposed.
        },
      }).catch((err) => {
        const text = describeMicError(err);
        liveCallRef.current.phase = 'done';
        patchLiveCall(call?.id, { status: 'failed', errorMessage: text });
        setPhoneCallContext((prev) => ({
          ...(prev || {}),
          status: 'failed',
          statusMessage: text,
        }));
        toast.error(text);
      });
    } catch (error) {
      const code = error.response?.data?.code;
      const raw = error.response?.data?.message || error.message || 'Failed to start browser call';
      const message = error.response?.data?.message ? raw : describeMicError(error);
      liveCallRef.current.phase = 'done';
      if ((code === 'RC_NOT_CONNECTED' || code === 'RC_MISSING_VOIP' || code === 'RC_RECONNECT') && !skipConnect) {
        toast.info('RingCentral login needs to be refreshed for browser calling.');
        startRingcentralConnect(toNumber);
        return;
      }
      toast.error(message);
      setPhoneCallContext({
        status: 'failed',
        statusMessage: message,
        summary: null,
        durationSeconds: null,
      });
      if (liveCallRef.current.callId) {
        patchLiveCall(liveCallRef.current.callId, { status: 'failed', errorMessage: message });
      }
    }
  };

  const handlePatientCall = async () => {
    const phone = resolvePatientDialNumber();
    if (!phone) {
      toast.info('No phone number on file for this patient.');
      return;
    }

    const targetNumber = toE164(phone);
    if (!targetNumber) {
      toast.error('Please enter a valid 10-digit US phone number in the patient profile.');
      return;
    }

    try {
      const statusRes = await ringcentralService.getStatus();
      const rc = statusRes?.data || {};

      if (!rc.enabled || !rc.canCall) {
        window.location.href = `tel:${String(targetNumber).replace(/[^\d+]/g, '')}`;
        return;
      }

      if (!rc.configured) {
        toast.info('RingCentral credentials are still placeholders. Opening the follow-up note until Client ID/Secret are added.');
        openPhoneCallFollowUp({
          status: 'queued',
          statusMessage: 'RingCentral is not configured yet. Replace dummy Client ID/Secret in backend/.env, then click Call again.',
          summary: null,
          durationSeconds: null,
        });
        return;
      }

      if (!rc.connected) {
        toast.info('Connect your RingCentral account to call this patient.');
        startRingcentralConnect(targetNumber);
        return;
      }

      await placePatientRingcentralCall({ toNumber: targetNumber });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not start RingCentral call');
      if (targetNumber) {
        window.location.href = `tel:${String(targetNumber).replace(/[^\d+]/g, '')}`;
      }
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rc = params.get('ringcentral');
    if (!rc) return undefined;

    const nextPath = `${location.pathname}${location.hash || ''}`;
    const clearQuery = () => navigate(nextPath, { replace: true });

    if (rc === 'connected') {
      toast.success('RingCentral connected');
      let pending = null;
      try {
        pending = JSON.parse(sessionStorage.getItem(RC_PENDING_CALL_KEY) || 'null');
      } catch {
        pending = null;
      }
      sessionStorage.removeItem(RC_PENDING_CALL_KEY);
      clearQuery();
      if (
        pending
        && String(pending.practiceId) === String(practiceId)
        && String(pending.patientId) === String(patientId)
      ) {
        placePatientRingcentralCall({ toNumber: pending.toNumber, skipConnect: true });
      }
      return undefined;
    }

    if (rc === 'error') {
      toast.error(params.get('message') || 'RingCentral connection failed');
      sessionStorage.removeItem(RC_PENDING_CALL_KEY);
      clearQuery();
    }
    return undefined;
  }, [location.search, location.pathname, location.hash, navigate, practiceId, patientId]);

  useEffect(() => {
    if (!activeRingCentralCallId) return undefined;
    let cancelled = false;
    let intervalId;
    const poll = async () => {
      try {
        const response = await ringcentralService.getCall(activeRingCentralCallId);
        const call = response?.data;
        if (cancelled || !call) return;
        setPhoneCallContext((prev) => {
          const live = ['connecting', 'calling', 'connected'].includes(liveCallRef.current.phase);
          const prevStatus = prev?.status;
          if (live && ['failed', 'ended', 'summarizing', 'summarized'].includes(call.status)) {
            return {
              ...(prev || {}),
              toNumber: call.toNumber || prev?.toNumber || null,
              fromNumber: call.fromNumber || prev?.fromNumber || null,
            };
          }
          if (prevStatus === 'failed' && call.status !== 'failed') {
            return prev;
          }
          if (['summarized', 'summarizing'].includes(prevStatus)
            && !['summarized', 'summarizing'].includes(call.status)) {
            return {
              ...prev,
              summary: call.summaryText || prev?.summary || null,
              durationSeconds: call.durationSeconds ?? prev?.durationSeconds,
            };
          }
          if (call.status === 'connected' && liveCallRef.current.phase !== 'done') {
            liveCallRef.current.phase = 'connected';
          }
          return {
            status: call.status,
            statusMessage: call.statusMessage || '',
            summary: call.summaryText || null,
            durationSeconds: call.durationSeconds,
            toNumber: call.toNumber || prev?.toNumber || null,
            fromNumber: call.fromNumber || prev?.fromNumber || null,
          };
        });
        if (
          liveCallRef.current.phase === 'done'
          && (call.status === 'summarized' || (call.status === 'failed' && call.errorMessage))
        ) {
          clearInterval(intervalId);
        }
      } catch {
        // Keep the modal usable even if a poll fails.
      }
    };
    poll();
    intervalId = setInterval(poll, 4000);
    const timeoutId = setTimeout(() => clearInterval(intervalId), 20 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [activeRingCentralCallId]);

  useEffect(() => {
    const onPageHide = () => {
      disposeWebPhone().catch(() => {});
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      disposeWebPhone().catch(() => {});
    };
  }, []);

  const handleEditFollowUpNote = (note) => {
    setEditingFollowUpNote(note);
    followUpWasRunningRef.current = Boolean(
      (programTimers[activeProgramKey] || DEFAULT_PROGRAM_TIMERS[activeProgramKey])?.isRunning
    );
    updateActiveProgramTimer({ isRunning: false });
    setShowFollowUpModal(true);
  };

  const handleFollowUpTimerSync = (seconds) => {
    const nextSeconds = Math.max(0, Number(seconds) || 0);
    // Always resume the session timer after follow-up closes (modal owns the clock while open)
    updateActiveProgramTimer({
      seconds: nextSeconds,
      isRunning: true
    });
    followUpWasRunningRef.current = false;
  };

  const handleTimerMinutesChange = (value) => {
    const cleaned = String(value ?? '').replace(/\D/g, '');
    const nextMinutes = cleaned === '' ? 0 : parseInt(cleaned, 10);
    const safeMinutes = Number.isFinite(nextMinutes) ? Math.max(0, nextMinutes) : 0;
    const currentSeconds = timer % 60;
    updateActiveProgramTimer({ seconds: (safeMinutes * 60) + currentSeconds });
  };

  const handleTimerSecondsChange = (value) => {
    const cleaned = String(value ?? '').replace(/\D/g, '');
    const nextSeconds = cleaned === '' ? 0 : parseInt(cleaned, 10);
    const safeSeconds = Number.isFinite(nextSeconds) ? Math.min(59, Math.max(0, nextSeconds)) : 0;
    const currentMinutes = Math.floor(timer / 60);
    updateActiveProgramTimer({ seconds: (currentMinutes * 60) + safeSeconds });
  };

  const resetTimer = () => {
    const next = {
      ...programTimers,
      [activeProgramKey]: { seconds: 0, isRunning: false }
    };
    setProgramTimers(next);
    persistTimers(timerStorageKey, { ...next, activeProgram: activeProgramKey });
    setShowResetTimerModal(false);
  };

  const openResetTimerModal = () => {
    setShowResetTimerModal(true);
  };

  const cancelResetTimer = () => {
    setShowResetTimerModal(false);
  };

  const handleProgramTabChange = (program) => {
    const nextProgram = String(program || '').toUpperCase();
    if (!isProfileProgram(nextProgram) || nextProgram === activeProgramTab) return;
    setActiveProgramTab(nextProgram);
  };

  const handleOpenEditPatientModal = async () => {
    if (editPatientLoading) return;
    setEditPatientLoading(true);
    try {
      const resp = await patientService.getPatient(practiceId, patientId);
      const fullPatient = resp?.data?.data || resp?.data || patientData?.patient || patientData;
      setPatientToEdit(fullPatient);
      setShowEditPatientModal(true);
    } catch (err) {
      console.warn('[PatientDetails] Could not fetch full patient for edit', err);
      setPatientToEdit(patientData?.patient || patientData);
      setShowEditPatientModal(true);
    } finally {
      setEditPatientLoading(false);
    }
  };


  const getStatusBadge = (status) => {
    if (!status) {
      return <span className="patient-details-status-badge discontinue">N/A</span>;
    }

    // Normalize status to string for safe comparisons.
    const normalized = typeof status === 'number' ? String(status) : String(status).trim();
    const statusLower = normalized.toLowerCase();

    // Convert numeric codes -> alphabet labels
    // (matches PatientManagement: 2=Active, 3=Pending, 4=Locked)
    const numericStatus = /^\d+$/.test(statusLower) ? parseInt(statusLower, 10) : null;
    if (numericStatus !== null) {
      if (numericStatus === 2) {
        return <span className="patient-details-status-badge active">Active</span>;
      }
      if (numericStatus === 3) {
        // No dedicated CSS class for "pending", so reuse the "paused" color.
        return <span className="patient-details-status-badge paused">Pending</span>;
      }
      if (numericStatus === 4) {
        return <span className="patient-details-status-badge lock">Locked</span>;
      }
    }

    const statusMap = {
      'active': { class: 'active', text: 'Active' },
      'pending': { class: 'paused', text: 'Pending' },
      'paused': { class: 'paused', text: 'Paused' },
      'locked': { class: 'lock', text: 'Locked' },
      'discontinue': { class: 'discontinue', text: 'N/A' },
      'inactive': { class: 'discontinue', text: 'Inactive' }
    };
    const statusInfo = statusMap[statusLower] || { class: 'discontinue', text: status || 'N/A' };
    return <span className={`patient-details-status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const getGenderText = (gender) => {
    if (gender === 1 || gender === '1' || String(gender).toLowerCase() === 'male') {
      return 'Male';
    }
    if (gender === 0 || gender === '0' || gender === 2 || gender === '2' || String(gender).toLowerCase() === 'female') {
      return 'Female';
    }
    if (gender === 3 || gender === '3' || String(gender).toLowerCase() === 'other') {
      return 'Other';
    }
    return 'N/A';
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || 'P';
  };

  const formatNameLastFirst = (fullName) => {
    const raw = String(fullName || '').trim();
    if (!raw) return '-';
    if (raw.includes(',')) return raw;
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0];
    const first = parts.shift();
    const last = parts.join(' ');
    return `${last}, ${first}`;
  };

  const getBPStatus = (bpData) => {
    if (!bpData) return { sysStatus: 'normal', diaStatus: 'normal', isOutOfRange: false };
    const target =
      getBloodPressureTargetForMeasurement(bpData, scheduleTargets) ||
      DEFAULT_TARGETS.bloodPressureTargetRange[0];
    return checkBPValues(bpData, target);
  };

  const getBGStatus = (bgData) => {
    if (!bgData) return 'normal';
    const target =
      getBloodGlucoseTargetForMeasurement(bgData, scheduleTargets) ||
      DEFAULT_TARGETS.bloodGlucoseTargetRange[0];
    const val = Number(
      bgData.blood_glucose_value_1 ??
      bgData.blood_glucose_value_2 ??
      bgData.blood_glucose_value_3 ??
      bgData.blood_glucose_value ??
      bgData.glucose_value
    );
    return checkBGValue(val, target);
  };

  const getWeightTargetRange = () =>
    (scheduleTargets?.weightTargetRange &&
      Object.keys(scheduleTargets.weightTargetRange).length > 0 &&
      scheduleTargets.weightTargetRange) ||
    DEFAULT_TARGETS.weightTargetRange;

  const getWeightStatus = (weightData) => {
    if (!weightData) return 'normal';
    return checkWeightValue(Number(weightData.weight), getWeightTargetRange());
  };

  const getBodyFatStatus = (weightData) => {
    if (!weightData) return 'normal';
    const fat = weightData.fat ?? weightData.body_fat;
    if (fat == null || fat === '') return 'normal';
    const fatValue = Number(fat);
    if (Number.isNaN(fatValue)) return 'normal';
    const target = getWeightTargetRange();
    const fatMin = Number(target.bodyFatMin ?? 14);
    const fatMax = Number(target.bodyFatMax ?? 27);
    if (fatValue > fatMax) return 'high';
    if (fatValue < fatMin) return 'low';
    return 'normal';
  };

  const getBmiStatus = (weightData) => {
    if (!weightData || weightData.bmi == null || weightData.bmi === '') return 'normal';
    const target = getWeightTargetRange();
    return getBmiCategoryStatus(weightData.bmi, {
      bmiNormal: target.bmiNormal ?? 18.5,
      bmiOverweight: target.bmiOverweight ?? 25,
      bmiObese: target.bmiObese ?? 30
    });
  };

  const getPulseTargetBounds = (bpData) => {
    const target =
      getBloodPressureTargetForMeasurement(bpData, scheduleTargets) ||
      DEFAULT_TARGETS.bloodPressureTargetRange[0];
    return {
      pulseMin: Number(target?.pulseMin ?? 60),
      pulseMax: Number(target?.pulseMax ?? 100)
    };
  };

  if (loading) {
    return (
      <div className="patient-details-page">
        <div className="patient-details-main-container">
          <div className="patient-details-loading-stack">
            <ScreenSectionsSkeleton leftSections={3} rightSections={4} />
            <div className="patient-details-extra-form-skeletons">
              <FormDetailSkeleton titleLines={1} fieldRows={3} columns={2} showFooter={false} />
              <FormDetailSkeleton titleLines={1} fieldRows={2} columns={3} showFooter={false} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patient-details-page">
        <div className="patient-details-error-container">
          <div className="patient-details-error-message">{error}</div>
        </div>
      </div>
    );
  }

  if (!patientData || !patientData.patient) {
    return (
      <div className="patient-details-page">
        <div className="patient-details-error-container">
          <div className="patient-details-error-message">No patient data available</div>
        </div>
      </div>
    );
  }

  const { patient, latest_measurements, average_measurements, follow_up_notes, caregivers, device_serial, billing_compliance } = patientData;
  const bp = latest_measurements?.blood_pressure;
  const bg = latest_measurements?.blood_glucose;
  const weight = latest_measurements?.weight;
  const bgDisplayValue = bg
    ? Number(
      bg.blood_glucose_value_1 ??
      bg.blood_glucose_value_2 ??
      bg.blood_glucose_value_3 ??
      bg.blood_glucose_value ??
      bg.glucose_value
    )
    : null;

  const timerPillSet = (() => {
    const program = String(activeProgramTab || 'RPM').toUpperCase();
    const serviceTimeMinutes = billing_compliance?.service_time_minutes ?? 0;
    const rawPills =
      program === 'CCM'
        ? billing_compliance?.ccm_pills
        : program === 'PCM'
          ? billing_compliance?.pcm_pills
          : billing_compliance?.rpm_pills;
    if (Array.isArray(rawPills) && rawPills.length > 0) {
      return rawPills
        .map((p) => mapBillingPillToChip(p, { serviceTimeMinutes }))
        .filter(Boolean);
    }
    return SESSION_TIMER_PILLS_FALLBACK[program] || SESSION_TIMER_PILLS_FALLBACK.RPM;
  })();

  const completeTimerPillSet =
    String(activeProgramTab || 'RPM').toUpperCase() === 'RPM'
      ? RPM_PROFILE_CPT_ORDER.map(
          (code) => timerPillSet.find((pill) => pill.code === code)
            || buildRpmProfileFallbackPill(code, billing_compliance)
        )
      : timerPillSet;

  const timerPillSetDisplay = expand99458PillsForUi(completeTimerPillSet);

  const billingPeriodStartSource =
    activeProgramTab === 'CCM' || activeProgramTab === 'PCM'
      ? patient?.ccm_start_date || patient?.rpm_start_date || patient?.created_at
      : patient?.rpm_start_date || patient?.created_at;
  const billingPeriodFromPatient = billingPeriodStartSource
    ? getBillingPeriodFromStartDate(billingPeriodStartSource)
    : getCalendarMonthPeriod(new Date());
  const billingPeriod =
    billing_compliance?.dateFrom && billing_compliance?.dateTo
      ? { dateFrom: billing_compliance.dateFrom, dateTo: billing_compliance.dateTo }
      : billingPeriodFromPatient;
  const billingPeriodLabel =
    billingPeriod?.dateFrom && billingPeriod?.dateTo
      ? `${formatDateMMDDYYYY(billingPeriod.dateFrom)} – ${formatDateMMDDYYYY(billingPeriod.dateTo)}`
      : '—';

  const avgTabs = [
    {
      id: 'seven_days',
      title: '7 Days Average',
      data: average_measurements?.seven_days,
      start_date: average_measurements?.seven_days?.start_date,
      end_date: average_measurements?.seven_days?.end_date
    },
    {
      id: 'one_month',
      title: '1 Month Average',
      data: average_measurements?.one_month,
      start_date: average_measurements?.one_month?.start_date,
      end_date: average_measurements?.one_month?.end_date
    },
    {
      id: 'three_months',
      title: '3 Months Average',
      data: average_measurements?.three_months,
      start_date: average_measurements?.three_months?.start_date,
      end_date: average_measurements?.three_months?.end_date
    }
  ];

  const patientFullName = `${patient.last_name || ''}, ${patient.first_name || ''}`.trim();
  const practiceName = patient.practice_name || 'Practice';
  const initials = getInitials(patient.first_name, patient.last_name);
  const patientRecordId = patient.patients_table_id ?? patient.id ?? patientId;

  // Determine alert colors for last-upload vitals (from schedule/target settings)
  const bpStatus = getBPStatus(bp);
  const bgStatus = getBGStatus(bg);
  const weightStatus = getWeightStatus(weight);
  const bodyFatStatus = getBodyFatStatus(weight);
  const bmiStatus = getBmiStatus(weight);
  const pulseTargetBounds = getPulseTargetBounds(bp);
  const pulseStatus = checkPulseValue(bp?.pulse, pulseTargetBounds) || 'normal';

  // Helper function to check if a vital is configured for monitoring
  const isVitalConfigured = (vitalType) => {
    const parsed = parsePatientVitals(patient.vitals || patient.monitoring_types || patient.monitoringTypes || '');
    if (vitalType === 'BP' || vitalType === 'Blood Pressure') return parsed.bp;
    if (vitalType === 'BG' || vitalType === 'Blood Glucose') return parsed.bg;
    if (vitalType === 'WT' || vitalType === 'Weight') return parsed.wt;
    return false;
  };

  // Determine if vitals are configured (enable cards even if no data)
  const isBPConfigured = isVitalConfigured('BP') || isVitalConfigured('Blood Pressure');
  const isBGConfigured = isVitalConfigured('BG') || isVitalConfigured('Blood Glucose');
  const isWeightConfigured = isVitalConfigured('WT') || isVitalConfigured('Weight');
  const practiceEnabledMonitoring = Array.isArray(patientData?.practice_monitoring_types)
    ? getPracticeMonitoringSectionFlags(patientData.practice_monitoring_types)
    : null;

  const getSummaryRange = (tabId) => {
    const endDate = new Date();
    const startDate = new Date(endDate);

    if (tabId === 'seven_days') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (tabId === 'one_month') {
      startDate.setMonth(startDate.getMonth() - 1);
    } else if (tabId === 'three_months') {
      startDate.setMonth(startDate.getMonth() - 3);
    }

    return {
      start: formatDateMDY(startDate),
      end: formatDateMDY(endDate)
    };
  };

  const caregiverDisplayValue = caregivers && caregivers.length > 0
    ? caregivers.map((cg) => formatNameLastFirst(cg.full_name || `${cg.last_name || ''} ${cg.first_name || ''}`)).join(', ')
    : formatNameLastFirst(patient.caregiver_name);

  const renderCCMContent = () => {
    const enrollmentId = patient.ccm_enrollment_id || patient.id;
    const ccmPatientPk = patient.patients_table_id ?? patient.id ?? patientId;

    const conditions = normalizePatientChronicConditions(patient.icd_codes);
    const carePlanApproved = Boolean(ccmEnrollmentSummary?.has_approved_care_plan);
    const carePlanStatus = ccmEnrollmentSummary?.care_plan_status;
    const carePlanName = ccmEnrollmentSummary?.care_plan_name;
    const isPlaceholderPlan = !carePlanName || carePlanName === 'Comprehensive CCM Care Plan';
    const carePlanStarted = !isPlaceholderPlan && (carePlanApproved || carePlanStatus === 'draft' || carePlanStatus === 'active');
    const carePlanLead = !carePlanStarted
      ? 'Not started'
      : carePlanApproved
        ? 'Approved'
        : carePlanStatus === 'draft'
          ? 'Draft'
          : 'Unsigned';
    const carePlanNote = !carePlanStarted
      ? 'required for CCM billing'
      : carePlanApproved
        ? (carePlanName || 'ready for CCM billing')
        : 'provider must sign and approve for billing';

    const CCM_SUB_TABS = [
      { key: 'overview', label: 'Overview' },
    ];

    return (
      <div className="pd-ccm-root">
        <div className="patient-details-sub-tabs" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {CCM_SUB_TABS.map(t => (
              <button
                key={t.key}
                className={`patient-details-sub-tab ${ccmSubTab === t.key ? 'active' : ''}`}
                onClick={() => setCcmSubTab(t.key)}
                type="button"
              >
                {t.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
            {renderCareProgramTabs()}
          </div>
        </div>

        <div className="pd-ccm-content">
          {ccmSubTab === 'overview' && (
            <>
              <div className="pcm-summary">
                <div className="pcm-summary-row pcm-summary-row--enrollment">
                  <span className="pcm-summary-label">CCM enrollment</span>
                  <span className="pcm-summary-active">
                    <span className="pcm-summary-dot" aria-hidden="true" />
                    Active
                  </span>
                </div>
                <div className="pcm-summary-row">
                  <span className="pcm-summary-label">Chronic conditions</span>
                  {conditions.length > 0 ? (
                    <div className="pcm-summary-pills">
                      {conditions.map((condition, index) => {
                        const label = [condition.icd_code, condition.condition_name].filter(Boolean).join(' · ');
                        return (
                          <span key={`${condition.icd_code || 'condition'}-${index}`} className="pcm-summary-pill">
                            {label || 'Condition'}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="pcm-summary-muted">Requires 2 chronic conditions</span>
                  )}
                </div>
                <div className="pcm-summary-row pcm-summary-row--plan-head">
                  <span className="pcm-summary-label">Care plan</span>
                  {!isPatientSelfView ? (
                    <button type="button" className="pcm-summary-create" onClick={() => ccmPlanRef.current?.openCreate()}>
                      Create care plan
                    </button>
                  ) : null}
                </div>
                {ccmSavedPlans.length === 0 ? (
                  <div className="pcm-summary-row pcm-summary-row--plan">
                    <span className="pcm-summary-status">
                      <strong>{carePlanLead}</strong>
                      <span> · {carePlanNote}</span>
                    </span>
                  </div>
                ) : ccmSavedPlans.map((plan, index) => (
                  <div className="pcm-summary-row pcm-summary-row--plan" key={plan.instanceId || index}>
                    <span className="pcm-summary-status">
                      <strong>Saved</strong>
                      <span> · {plan.label || 'Care plan'}</span>
                    </span>
                    <div className="pcm-summary-actions">
                      <ActionButtons
                        onView={() => ccmPlanRef.current?.viewPlan(index)}
                        onEdit={isPatientSelfView ? undefined : () => ccmPlanRef.current?.editPlan(index)}
                        onDelete={isPatientSelfView ? undefined : () => ccmPlanRef.current?.deletePlan(index)}
                        hideEdit={isPatientSelfView}
                        hideDelete={isPatientSelfView}
                      />
                    </div>
                  </div>
                ))}
              </div>

            <CcmComplexityAttestationPanel
              patientId={ccmPatientPk}
              practiceId={practiceId}
              userRoleId={user?.role_id}
              refreshKey={ccmBillingRefreshKey}
              onAttestationUpdated={() => {
                setCcmBillingRefreshKey((k) => k + 1);
                loadCcmEnrollmentSummary();
              }}
            />

            </>
          )}

          <div className="pd-ccm-panel" style={{ display: 'none' }}>
            <CarePlanPanel
              ref={ccmPlanRef}
              enrollmentId={String(ccmEnrollmentSummary?.enrollment_id || enrollmentId)}
              patient={patient}
              readOnly={isPatientSelfView}
              onBillingRefresh={() => {
                setCcmBillingRefreshKey((k) => k + 1);
                loadCcmEnrollmentSummary();
              }}
              onPlansChange={handleCcmPlansChange}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderPCMContent = () => (
    <PatientPcmProgramPane
      practiceId={practiceId}
      patientId={patientId}
      patient={patient ? { ...patient, caregivers: caregivers || [] } : patient}
      enrollmentSummary={ccmEnrollmentSummary}
      formatDateShort={formatDateShort}
      programTabs={renderCareProgramTabs()}
      onPatientIcdUpdate={(icdCodes) => {
        setPatientData((prev) => ({
          ...prev,
          patient: { ...prev.patient, icd_codes: icdCodes },
        }));
      }}
      onBillingRefresh={() => {
        setCcmBillingRefreshKey((k) => k + 1);
        loadCcmEnrollmentSummary();
      }}
      followUpNotes={patientData?.follow_up_notes || []}
      practiceCaregivers={practiceCaregivers}
      practiceProviders={practiceProviders}
      currentUser={user}
      onAddFollowUp={openFollowUpModal}
      onEditFollowUp={handleEditFollowUpNote}
      isPatientSelfView={isPatientSelfView}
    />
  );

  const renderCareProgramTabs = () => {
    const programs = (patientData?.care_programs || patientData?.patient?.care_programs) || [];
    const supported = programs.map(prog => {
      const p = typeof prog === 'string' ? prog : (prog.short_name || '');
      return p.toUpperCase();
    }).filter(pUpper => isProfileProgram(pUpper));

    // Only render program selector buttons if enrolled in multiple (e.g. RPM and PCM)
    if (supported.length <= 1) return null;

    return (
      <div className="pdx-mode-group" role="tablist" aria-label="Care program">
        {supported.map(pUpper => {
          const isActive = activeProgramTab === pUpper;
          return (
            <button
              key={pUpper}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`pdx-chip${isActive ? ' active' : ''}`}
              onClick={() => handleProgramTabChange(pUpper)}
            >
              {pUpper}
            </button>
          );
        })}
      </div>
    );
  };

  const renderRPMOverview = () => (
    <PatientRpmOverview
      practiceId={practiceId}
      patientId={patientId}
      fromDashboard={location.state?.fromDashboard}
      activeProgram={activeProgramTab}
      navigate={navigate}
      isBPConfigured={isBPConfigured}
      isBGConfigured={isBGConfigured}
      isWeightConfigured={isWeightConfigured}
      bp={bp}
      bg={bg}
      weight={weight}
      bgDisplayValue={bgDisplayValue}
      bpStatus={bpStatus}
      bgStatus={bgStatus}
      weightStatus={weightStatus}
      bodyFatStatus={bodyFatStatus}
      bmiStatus={bmiStatus}
      pulseStatus={pulseStatus}
      pulseTargetMin={pulseTargetBounds.pulseMin}
      pulseTargetMax={pulseTargetBounds.pulseMax}
      getVitalDisplayDateTime={getVitalDisplayDateTime}
      deviceSerial={
        (patientMeters || [])
          .map((meter) => [meter.model_number, meter.serial_number].filter(Boolean).join(' · '))
          .filter(Boolean)
          .join(', ')
        || device_serial
        || patientData?.patient?.serial_number
        || 'N/A'
      }
      patientData={patientData}
      scheduleTargets={scheduleTargets}
      onRefresh={() => fetchPatientDetails({ includeNotes: false, showLoader: false })}
      isReadOnly={isPatientSelfView}
      caregiverOptions={practiceCaregivers}
      providerOptions={practiceProviders}
      onReviewStatusChange={handleAbnormalReviewStatusChange}
    />
  );

  return (
    <div className="patient-details-page">
      <div className="patient-details-page-content">
        <section className="pdx-card">
          <PatientDemographicsBanner
            patient={patient}
            patientFullName={patientFullName}
            getGenderText={getGenderText}
            formatNameLastFirst={formatNameLastFirst}
            deviceSerial={device_serial}
            consentDate={
              ccmEnrollmentSummary?.consent_date || patient?.ccm_consent_date || null
            }
            activeProgram={activeProgramTab}
            showContactActions={!isSuperAdmin && !isPatientSelfView}
            onCall={handlePatientCall}
            onEmail={() => {
              const patientUserId =
                patient?.user_id ?? patient?.userId ?? null;
              if (patientUserId) {
                navigate(`/chat?with=${encodeURIComponent(patientUserId)}`);
                return;
              }
              navigate('/chat');
            }}
            onEdit={isPatientSelfView ? undefined : handleOpenEditPatientModal}
            practiceId={practiceId}
            patientId={patientId}
            billingPeriodLabel={billingPeriodLabel}
            timer={timer}
            formatTimer={formatTimer}
            isRunning={isRunning}
            startTimer={startTimer}
            pauseTimer={pauseTimer}
            openResetTimerModal={openResetTimerModal}
            timerPillSetDisplay={timerPillSetDisplay}
            SessionCptChip={SessionCptChip}
            isPatientSelfView={isPatientSelfView}
            profilePicUrl={patientProfilePicUrl}
          />

        <div className="patient-details-body-grid">
          <div className="patient-details-left-column">
          <div className="ppa-analytics-wrap patient-details-program-panel">
            <div className="patient-details-main-layout" style={{ display: "block" }}>
              <div className="patient-details-main-column">
              <div
                className="patient-details-program-pane"
                hidden={activeProgramTab !== 'RPM'}
                aria-hidden={activeProgramTab !== 'RPM'}
              >
                  <div className="patient-details-sub-tabs" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div ref={subTabsWrapRef} className={`patient-details-sub-tabs-wrap${subIndicatorReady ? ' patient-details-sub-tabs-wrap--ready' : ''}`} style={{ position: "relative", display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: 0 }}>
                      <span
                        className={`patient-details-sub-tab-indicator${subIndicatorReady ? ' patient-details-sub-tab-indicator--ready' : ''}`}
                        style={subTabIndicatorStyle}
                        aria-hidden="true"
                      />
                      <button
                        className={`patient-details-sub-tab ${activeRpmSubTab === 'Overview' ? 'active' : ''}`}
                        onClick={() => {
                          setShowScheduleEventLogs(false);
                          setScheduleEventLogSection(null);
                          setActiveRpmSubTab('Overview');
                        }}
                      >
                        Overview
                      </button>
                      <button
                        className={`patient-details-sub-tab ${activeRpmSubTab === 'Manage meter' ? 'active' : ''}`}
                        onClick={() => {
                          setShowScheduleEventLogs(false);
                          setScheduleEventLogSection(null);
                          setActiveRpmSubTab('Manage meter');
                        }}
                        title="Manage meter"
                      >
                        Meter
                      </button>
                      <button
                        className={`patient-details-sub-tab ${activeRpmSubTab === 'Schedule & target settings' ? 'active' : ''}`}
                        onClick={() => {
                          setShowScheduleEventLogs(false);
                          setScheduleEventLogSection(null);
                          setActiveRpmSubTab('Schedule & target settings');
                        }}
                        title="Schedule & target settings"
                      >
                        Schedule
                      </button>
                      <button
                        className={`patient-details-sub-tab ${activeRpmSubTab === 'Follow up' ? 'active' : ''}`}
                        onClick={() => {
                          setShowScheduleEventLogs(false);
                          setScheduleEventLogSection(null);
                          setActiveRpmSubTab('Follow up');
                        }}
                      >
                        Follow up
                      </button>
                      <button
                        className={`patient-details-sub-tab ${activeRpmSubTab === 'Documents' ? 'active' : ''}`}
                        onClick={() => {
                          setShowScheduleEventLogs(false);
                          setScheduleEventLogSection(null);
                          setActiveRpmSubTab('Documents');
                        }}
                      >
                        Documents
                      </button>
                      <button
                        className={`patient-details-sub-tab ${activeRpmSubTab === 'Care Plan' ? 'active' : ''}`}
                        onClick={() => {
                          setShowScheduleEventLogs(false);
                          setScheduleEventLogSection(null);
                          setActiveRpmSubTab('Care Plan');
                        }}
                      >
                        Care plan
                      </button>
                    </div>
                    <div className="patient-details-sub-tabs-trailing">
                      {renderCareProgramTabs()}
                    </div>
                  </div>
                  <div className="patient-details-sub-content">
                    {activeRpmSubTab === 'Care Plan' && (
                      <RpmCarePlanTab
                        practiceId={practiceId}
                        patientId={patientId}
                        patient={patient}
                        patientFullName={patientFullName}
                        getGenderText={getGenderText}
                        formatNameLastFirst={formatNameLastFirst}
                        caregiverDisplayValue={caregiverDisplayValue}
                        latestVitals={getLatestVitalsSummary({
                          bp,
                          bg,
                          bgDisplayValue,
                          weight,
                          bpStatus,
                          pulseStatus,
                        })}
                        enabledMonitoring={{
                          bp: isBPConfigured,
                          bg: isBGConfigured,
                          weight: isWeightConfigured,
                        }}
                        isReadOnly={isPatientSelfView}
                      />
                    )}

                    {activeRpmSubTab === 'Overview' && (
                      <div className="patient-details-sub-pane pdx-overview-pane">
                        {renderRPMOverview()}
                      </div>
                    )}

                    {activeRpmSubTab === 'Manage meter' && (
                      <div className="patient-details-sub-pane">
                        {!isPatientSelfView ? (
                        <div className="add-patient-section-wrapper mt-0">
                          <div className="pdx-pane-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                            <div>
                              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f2942' }}>Meter Mapping — RPM</h3>
                            </div>
                          </div>
                          <div className="add-patient-section-content">
                            <div className="add-patient-meter-mapping-row" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                              <div className="add-patient-form-group" style={{ flex: '0.7 1 0', minWidth: '140px' }}>
                                <label>Product Name</label>
                                <select value={addMeterForm.model_number} onChange={(e) => setAddMeterForm({ ...addMeterForm, model_number: e.target.value })} className="add-patient-form-control">
                                  <option value="">- Select -</option>
                                  {products.map(p => <option key={p.id || p.model_number} value={p.model_number}>{formatMeterProductOptionLabel(p)}</option>)}
                                </select>
                              </div>
                              <div className="add-patient-form-group" style={{ flex: '1' }}>
                                <label>Extension</label>
                                <select value={addMeterForm.extension} onChange={(e) => setAddMeterForm({ ...addMeterForm, extension: e.target.value })} className="add-patient-form-control">
                                  <option value="">- Select -</option>
                                  {METER_EXTENSION_OPTIONS.map(ext => (
                                    <option key={ext.value} value={ext.value}>{ext.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="add-patient-form-group" style={{ flex: '0 0 230px', width: '230px', minWidth: '230px', maxWidth: '230px' }}>
                                <label>Serial number</label>
                                <input type="text" value={addMeterForm.serial_number} maxLength={16} onChange={(e) => setAddMeterForm({ ...addMeterForm, serial_number: normalizeDeviceSerialInput(e.target.value) })} className="add-patient-form-control" placeholder="16-character alphanumeric" />
                              </div>
                              <div className="add-patient-form-group" style={{ flex: '1' }}>
                                <label>Gateway serial number</label>
                                <input type="text" value={addMeterForm.gateway_serial_number} onChange={(e) => setAddMeterForm({ ...addMeterForm, gateway_serial_number: e.target.value })} className="add-patient-form-control" />
                              </div>
                              <div className="add-patient-action-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <button
                                  type="button"
                                  onClick={handleAddMeterInline}
                                  style={{
                                    height: '38px',
                                    background: '#03045E',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '0 18px',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.15s ease'
                                  }}
                                >
                                  <FontAwesomeIcon icon={faPlus} /> Add meter
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        ) : null}

                        <div className={`add-patient-section-wrapper${isPatientSelfView ? ' mt-0' : ' mt-4'}`}>
                          <div className="pdx-pane-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                            <div>
                              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f2942' }}>Mapped Devices</h3>
                            </div>
                          </div>
                          <div className="manage-meter-table-wrap">
                            {loadingMeters ? (
                              <div className="text-center py-4"><FontAwesomeIcon icon={faSpinner} spin /> Loading...</div>
                            ) : (
                              <table className="table table-hover mb-0" style={{ fontSize: '13px', width: '100%', tableLayout: 'fixed' }}>
                                <thead>
                                  <tr style={{ background: '#2E4A78', color: '#ffffff' }}>
                                    <th style={{ width: '50px', padding: '11px 14px', textAlign: 'left', color: '#ffffff', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>#</th>
                                    <th style={{ width: '16%', padding: '11px 14px', textAlign: 'left', color: '#ffffff', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Product Name</th>
                                    <th style={{ width: '15%', padding: '11px 14px', textAlign: 'left', color: '#ffffff', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Extension</th>
                                    <th style={{ width: '29%', padding: '11px 14px', textAlign: 'left', color: '#ffffff', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Serial No</th>
                                    <th style={{ width: '25%', padding: '11px 14px', textAlign: 'left', color: '#ffffff', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Gateway No</th>
                                    {!isPatientSelfView ? (
                                    <th style={{ width: '120px', padding: '11px 14px', textAlign: 'center', color: '#ffffff', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Action</th>
                                    ) : null}
                                  </tr>
                                </thead>
                                <tbody>
                                  {patientMeters && patientMeters.length > 0 ? (
                                    patientMeters.map((m, idx) => (
                                      <tr key={m.id || idx}>
                                        <td style={{ paddingLeft: '15px', textAlign: 'left', verticalAlign: 'middle' }}>{idx + 1}</td>
                                        <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{resolveMeterProductDisplayLabel(m.model_number, products)}</td>
                                        <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{formatMeterExtensionLabel(m.extension)}</td>
                                        <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{m.serial_number || 'N/A'}</td>
                                        <td style={{ paddingRight: isPatientSelfView ? '15px' : undefined, textAlign: 'left', verticalAlign: 'middle' }}>{m.gateway_serial_number || 'N/A'}</td>
                                        {!isPatientSelfView ? (
                                        <td style={{ paddingRight: '15px', textAlign: 'center', verticalAlign: 'middle' }}>
                                          <div className="d-flex justify-content-center">
                                            <ActionButtons
                                              hideView
                                              onEdit={() => handleEditMeterRow(m)}
                                              onDelete={() => handleDeleteMeterRow(m)}
                                            />
                                          </div>
                                        </td>
                                        ) : null}
                                      </tr>
                                    ))
                                  ) : (
                                    <tr><td colSpan={isPatientSelfView ? 5 : 6} className="text-center py-4 text-muted">No devices mapped</td></tr>
                                  )}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {activeRpmSubTab === 'Schedule & target settings' && (
                      <div className={`patient-details-sub-pane${showScheduleEventLogs && !isPatientSelfView ? ' pdx-pane-padded' : ' pdx-overview-pane'}`}>
                        {showScheduleEventLogs && !isPatientSelfView ? (
                          <PracticeInlineEventLogs
                            moduleId="Patient Schedule"
                            practiceId={practiceId}
                            targetUserId={patient?.user_id ?? patient?.userId ?? null}
                            patientId={patientRecordId}
                            sectionFilter={scheduleEventLogSection}
                          />
                        ) : (
                          <ScheduleTargetSettings
                            practiceId={practiceId}
                            patientId={patientId}
                            isReadOnly={practiceIsLocked || isPatientSelfView}
                            readOnlyMessage={
                              isPatientSelfView
                                ? 'Schedule and target settings are view-only.'
                                : undefined
                            }
                            showSectionEventLogButtons={!isPatientSelfView}
                            onSaved={() => fetchScheduleTargets()}
                            patientVitals={patient?.vitals || ''}
                            enabledMonitoring={{
                              bp: isBPConfigured,
                              bg: isBGConfigured,
                              weight: isWeightConfigured
                            }}
                            practiceEnabledMonitoring={practiceEnabledMonitoring}
                            onOpenEventLog={
                              isPatientSelfView
                                ? undefined
                                : (section) => {
                                    setScheduleEventLogSection(section || null);
                                    setShowScheduleEventLogs(true);
                                  }
                            }
                          />
                        )}
                      </div>
                    )}

                    {activeRpmSubTab === 'Follow up' && (
                      <div className="patient-details-sub-pane">
                        <div className="pdx-pane-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                          <div>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f2942' }}>
                              Follow-up Notes — RPM
                            </h3>
                          </div>
                          {!isPatientSelfView ? (
                            <button
                              type="button"
                              className="patient-details-btn-primary"
                              onClick={openFollowUpModal}
                              style={{ padding: '9px 16px', fontSize: '12.5px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#023d3d', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                            >
                              <FontAwesomeIcon icon={faPlus} /> Add Follow Up
                            </button>
                          ) : null}
                        </div>

                        <div className="patient-details-notes-list-new">
                          {paginatedFollowUpNotes && paginatedFollowUpNotes.length > 0 ? (
                            paginatedFollowUpNotes.map((note, index) => (
                              <div key={note.id || index} className="patient-details-note-item-new" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
                                <div className="note-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                  <span className="author" style={{ fontWeight: 700, color: '#0f2942', fontSize: '13px' }}>{note.author_name || `${note.last_name || ''}, ${note.first_name || ''}`.trim() || 'Unknown'}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span className="date" style={{ fontSize: '12px', color: '#64748b' }}>{note.date ? formatDateShort(note.date) : formatDateShort(note.created_at)}</span>
                                    {!isPatientSelfView ? (
                                      <button
                                        type="button"
                                        className="patient-details-btn-edit-note"
                                        onClick={() => handleEditFollowUpNote(note)}
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          borderRadius: '8px',
                                          border: '1px solid #dce4ec',
                                          background: 'transparent',
                                          color: '#64748b',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'pointer',
                                          fontSize: '13px',
                                          padding: 0,
                                          transition: 'all 0.15s ease'
                                        }}
                                        title="Edit Follow Up"
                                      >
                                        <FontAwesomeIcon icon={faPencilAlt} />
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                                <p className="note-content" style={{ margin: '6px 0', fontSize: '13.5px', color: '#334155' }}>{note.content || ''}</p>
                                <div className="note-footer" style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
                                  {formatFollowUpNoteFooter(note)}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="patient-details-note-item-new" style={{ padding: '32px', textAlign: 'center', background: '#f8fafc', borderRadius: '8px', border: '1px border-dashed #e2e8f0' }}>
                              <p className="text-muted" style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>No {activeProgramKey} follow-up notes found for this patient.</p>
                            </div>
                          )}
                        </div>

                        {sortedFollowUpNotes.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <ListScreenPaginationBar
                              entriesValue={followUpEntries}
                              onEntriesChange={(val) => {
                                setFollowUpEntries(val);
                                setFollowUpPage(1);
                              }}
                              currentPage={followUpPage}
                              totalRecords={sortedFollowUpNotes.length}
                              onPageChange={(page) => setFollowUpPage(page)}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {activeRpmSubTab === 'Documents' && (
                      <div className="patient-details-sub-pane">
                      
                        <PatientDocumentsSection
                          practiceId={practiceId}
                          patientId={patientId}
                          isEditMode
                          isReadOnly={isPatientSelfView}
                          showSavedTable
                          showUploadRows={!isPatientSelfView}
                          showUpdateButton={!isPatientSelfView}
                          title=""
                        />
                      </div>
                    )}

                  </div>
              </div>

              <div
                className="patient-details-program-pane"
                hidden={activeProgramTab !== 'CCM'}
                aria-hidden={activeProgramTab !== 'CCM'}
              >
                {renderCCMContent()}
              </div>
              <div
                className="patient-details-program-pane"
                hidden={activeProgramTab !== 'PCM'}
                aria-hidden={activeProgramTab !== 'PCM'}
              >
                {renderPCMContent()}
              </div>
              </div>
            </div>
          </div>
          </div>

        </div>
        </section>

        {!isPatientSelfView &&
          !(activeProgramTab === 'RPM' && activeRpmSubTab === 'Schedule & target settings') && (
          <aside
            id="patient-details-event-logs-end"
            className="patient-details-event-logs-page-end"
            aria-label="Patient event logs"
          >
            <button
              type="button"
              className="pd-logs-btn patient-details-event-log-tab-btn"
              onClick={() => setShowEventLogsModal(true)}
            >
              <FontAwesomeIcon icon={faList} className="patient-details-event-log-tab-btn__icon" />
              Event Logs
            </button>
          </aside>
        )}
      </div>

      {showEventLogsModal && !isPatientSelfView && createPortal(
        <div
          ref={eventLogsOverlayRef}
          className="modal-overlay patient-details-event-logs-modal-overlay"
          onClick={() => setShowEventLogsModal(false)}
        >
          <div
            ref={eventLogsContainerRef}
            className="modal-container patient-details-event-logs-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="patient-event-logs-modal-title"
          >
            <div className="modal-header">
              <h5 className="modal-title" id="patient-event-logs-modal-title">Event Logs</h5>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowEventLogsModal(false)}
                aria-label="Close event logs"
              >
                &times;
              </button>
            </div>
            <div className="modal-body patient-details-event-logs-modal-body">
              <div className="patient-details-event-log-filters" role="tablist" aria-label="Event log modules">
                {PATIENT_EVENT_LOG_FILTERS.map((filter) => {
                  const isActive = eventLogModuleFilter === filter.moduleId;
                  return (
                    <button
                      key={filter.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`patient-details-event-log-filter${isActive ? ' is-active' : ''}`}
                      onClick={() => setEventLogModuleFilter(filter.moduleId)}
                    >
                      {filter.label}
                    </button>
                  );
                })}
              </div>
              {patientRecordId ? (
                <EventLogSection
                  key={`${patientRecordId}-${eventLogModuleFilter || 'all'}`}
                  allModules={!eventLogModuleFilter}
                  modulePrefix={!eventLogModuleFilter ? 'Patient' : undefined}
                  moduleId={eventLogModuleFilter || undefined}
                  targetUserId="all"
                  practiceId={practiceId}
                  patientId={patientRecordId}
                />
              ) : (
                <p className="patient-details-event-logs-empty">Patient record is not available yet.</p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Follow-up Modal */}
      <AddFollowUpModal
        isOpen={showFollowUpModal}
        onClose={() => {
          if (hasActiveCall() || ['connecting', 'calling', 'connected'].includes(phoneCallContext?.status)) {
            hangupLiveRingcentralCall();
          }
          disposeWebPhone().catch(() => {});
          setShowFollowUpModal(false);
          setEditingFollowUpNote(null);
          setIsPhoneCallFollowUp(false);
          setPhoneCallContext(null);
          setActiveRingCentralCallId(null);
        }}
        practiceId={practiceId}
        patientId={patientId}
        patientName={patientFullName}
        patient={patientData?.patient || patient}
        onTimerSync={handleFollowUpTimerSync}
        followUpToEdit={editingFollowUpNote}
        isPhoneCall={isPhoneCallFollowUp && !editingFollowUpNote}
        phoneCallContext={phoneCallContext}
        onHangUp={() => hangupLiveRingcentralCall()}
        onSuccess={async () => {
          const pid = patientData?.patient?.patients_table_id || patientData?.patient?.id || patientId;
          notesCacheRef.current.delete(`${practiceId}:${pid}:${activeProgramKey}`);
          try {
            const followUpNotes = await fetchPatientNotes(patientData || {}, activeProgramKey);
            setPatientData((prev) => (prev ? { ...prev, follow_up_notes: followUpNotes } : prev));
          } catch {
            // ignore
          }
          // CPT service-time badges (99457/99458/etc.) update without full page refresh
          refreshBillingCompliance();
          if (activeProgramTab === 'CCM' || activeProgramTab === 'PCM') {
            loadCcmEnrollmentSummary();
          }
        }}
        initialTimerValue={activeTimer?.seconds || 0}
        careProgram={editingFollowUpNote?.care_program || activeProgramKey}
      />

      {showEditPatientModal && (
        <AddPatientModal
          practiceId={practiceId}
          patientToEdit={patientToEdit}
          defaultProgramTab={activeProgramTab}
          onClose={() => {
            setShowEditPatientModal(false);
            setPatientToEdit(null);
          }}
          onSave={() => {
            setShowEditPatientModal(false);
            setPatientToEdit(null);
            fetchPatientDetails({ includeNotes: false, showLoader: false });
          }}
        />
      )}
      {/* Add Meter Modal */}
      {showAddMeterModal && (
        <div className="patient-details-modal-overlay">
          <div className="patient-details-modal-content-wrapper-followup" onClick={(e) => e.stopPropagation()}>
            <div className="patient-details-modal-header-followup">
              <h3>Add Meter to Patient</h3>
              <button
                className="patient-details-modal-close-btn"
                onClick={() => {
                  setShowAddMeterModal(false);
                  setAddMeterForm({
                    model_number: '',
                    extension: '',
                    serial_number: '',
                    gateway_serial_number: ''
                  });
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="patient-details-modal-body-followup">
              <form onSubmit={handleAddMeter} style={{ width: '100%', padding: '20px' }}>
                <div className="patient-details-form-group">
                  <label>Model Number <span className="text-danger">*</span></label>
                  <select
                    className="patient-details-form-control"
                    value={addMeterForm.model_number}
                    onChange={(e) => setAddMeterForm({ ...addMeterForm, model_number: e.target.value })}
                    required
                  >
                    <option value="">- Select -</option>
                    {products.map((p) => (
                      <option key={p.id || p.model_number} value={p.model_number}>
                        {formatMeterProductOptionLabel(p)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="patient-details-form-group">
                  <label>Extension <span className="text-danger">*</span></label>
                  <select
                    className="patient-details-form-control"
                    value={METER_EXTENSION_OPTIONS.some((o) => o.value === String(addMeterForm.extension)) ? String(addMeterForm.extension) : ''}
                    onChange={(e) => setAddMeterForm({ ...addMeterForm, extension: e.target.value })}
                    required
                  >
                    <option value="">- Select -</option>
                    {METER_EXTENSION_OPTIONS.map((ext) => (
                      <option key={ext.value} value={ext.value}>{ext.label}</option>
                    ))}
                  </select>
                </div>
                <div className="patient-details-form-group">
                  <label>Serial Number <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="patient-details-form-control"
                    value={addMeterForm.serial_number}
                    maxLength={16}
                    onChange={(e) => setAddMeterForm({ ...addMeterForm, serial_number: normalizeDeviceSerialInput(e.target.value) })}
                    placeholder="16-character alphanumeric"
                    required
                  />
                </div>
                <div className="patient-details-form-group">
                  <label>Gateway Serial Number</label>
                  <input
                    type="text"
                    className="patient-details-form-control"
                    value={addMeterForm.gateway_serial_number}
                    onChange={(e) => setAddMeterForm({ ...addMeterForm, gateway_serial_number: e.target.value })}
                    placeholder="Gateway Serial Number (optional)"
                  />
                </div>
                <div className="patient-details-modal-footer">
                  <button
                    type="button"
                    className="patient-details-btn-cancel"
                    onClick={() => {
                      setShowAddMeterModal(false);
                      setAddMeterForm({
                        model_number: '',
                        extension: '',
                        serial_number: '',
                        gateway_serial_number: ''
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="patient-details-btn-save"
                  >
                    Add Meter
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Meter Modal — compact (same scale as Confirm Delete) */}
      {showEditMeterModal && (
        <div
          className="modal-overlay"
          onClick={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div className="modal-container patient-details-meter-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">Edit Device</h5>
              <button type="button" className="modal-close" onClick={cancelEditMeter} disabled={savingMeterEdit} aria-label="Close">&times;</button>
            </div>
            <form onSubmit={handleSaveEditMeter}>
              <div className="modal-body patient-details-meter-edit-modal-body">
                <div className="patient-details-form-group">
                  <label htmlFor="edit-meter-model">Model Number <span className="text-danger">*</span></label>
                  <select
                    id="edit-meter-model"
                    className="patient-details-form-control"
                    value={editMeterForm.model_number}
                    onChange={(e) => setEditMeterForm({ ...editMeterForm, model_number: e.target.value })}
                    required
                    disabled={savingMeterEdit}
                  >
                    <option value="">- Select -</option>
                    {products.map((p) => (
                      <option key={p.id || p.model_number} value={p.model_number}>
                        {formatMeterProductOptionLabel(p)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="patient-details-form-group">
                  <label>Extension <span className="text-danger">*</span></label>
                  <select
                    className="patient-details-form-control"
                    value={
                      METER_EXTENSION_OPTIONS.some((o) => o.value === String(editMeterForm.extension))
                        ? String(editMeterForm.extension)
                        : ''
                    }
                    onChange={(e) => setEditMeterForm({ ...editMeterForm, extension: e.target.value })}
                    required
                    disabled={savingMeterEdit}
                  >
                    <option value="">- Select -</option>
                    {METER_EXTENSION_OPTIONS.map((ext) => (
                      <option key={ext.value} value={ext.value}>{ext.label}</option>
                    ))}
                  </select>
                </div>
                <div className="patient-details-form-group">
                  <label>Serial Number <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="patient-details-form-control"
                    value={editMeterForm.serial_number}
                    maxLength={16}
                    onChange={(e) => setEditMeterForm({
                      ...editMeterForm,
                      serial_number: normalizeDeviceSerialInput(e.target.value)
                    })}
                    placeholder="16-character alphanumeric"
                    required
                    disabled={savingMeterEdit}
                  />
                </div>
                <div className="patient-details-form-group">
                  <label>Gateway Serial Number</label>
                  <input
                    type="text"
                    className="patient-details-form-control"
                    value={editMeterForm.gateway_serial_number}
                    onChange={(e) => setEditMeterForm({
                      ...editMeterForm,
                      gateway_serial_number: e.target.value
                    })}
                    placeholder="Optional"
                    disabled={savingMeterEdit}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={cancelEditMeter} disabled={savingMeterEdit}>
                  Cancel
                </button>
                <button type="submit" className="patient-details-btn-save" disabled={savingMeterEdit}>
                  {savingMeterEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Device Confirmation Modal */}
      {showDeleteDeviceModal && (
        <div
          className="modal-overlay"
          onClick={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button type="button" className="modal-close" onClick={cancelDeleteDevice} disabled={deletingDevice} aria-label="Close">&times;</button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete this device
                {deviceToDelete?.serial_number ? (
                  <> (<strong>{deviceToDelete.serial_number}</strong>)</>
                ) : null}
                ?
              </p>
              <p className="modal-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={cancelDeleteDevice} disabled={deletingDevice}>
                Cancel
              </button>
              <button type="button" className="btn-delete" onClick={confirmDeleteDevice} disabled={deletingDevice}>
                {deletingDevice ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Reset Session Timer Confirmation Modal */}
      {showResetTimerModal && (
        <div
          className="modal-overlay"
          onClick={cancelResetTimer}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div className="modal-container" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="reset-timer-modal-title">
            <div className="modal-header">
              <h5 className="modal-title" id="reset-timer-modal-title">Reset Session Timer</h5>
              <button type="button" className="modal-close" onClick={cancelResetTimer} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to reset the {activeProgramTab || 'RPM'} session timer
                {timer > 0 ? (
                  <> (<strong>{formatTimer(timer)}</strong>)</>
                ) : null}
                ?
              </p>
              <p className="modal-warning">This will clear the active session time and cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={cancelResetTimer}>
                Cancel
              </button>
              <button type="button" className="btn-delete" onClick={resetTimer}>
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Book Appointment Modal */}
      {showAppointmentModal && (
        <div className="patient-details-modal-overlay">
          <div className="patient-details-modal-content-wrapper-appointment" onClick={(e) => e.stopPropagation()}>
            <div className="patient-details-modal-header-appointment">
              <h3>Make an Appointment</h3>
              <button
                className="patient-details-modal-close-btn"
                onClick={() => {
                  setShowAppointmentModal(false);
                  setAppointmentData({
                    facility: '',
                    date: new Date().toISOString().split('T')[0],
                    startTime: '',
                    endTime: '',
                    provider: '',
                    caregiver: '',
                    visitType: '',
                    visitStatus: ''
                  });
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="patient-details-modal-body-appointment">
              <form>
                <div className="patient-details-appointment-form-grid">
                  <div className="patient-details-form-group">
                    <label>Facility</label>
                    <select
                      className="patient-details-form-control"
                      value={appointmentData.facility}
                      onChange={(e) => setAppointmentData({ ...appointmentData, facility: e.target.value })}
                    >
                      <option value="">Select Facility</option>
                    </select>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Provider</label>
                    <select
                      className="patient-details-form-control"
                      value={appointmentData.provider}
                      onChange={(e) => setAppointmentData({ ...appointmentData, provider: e.target.value })}
                    >
                      <option value="">Select Provider</option>
                    </select>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Date</label>
                    <div className="patient-details-date-input-wrapper">
                      <AppDateInput
                        className="patient-details-form-control patient-details-date-input"
                        value={appointmentData.date}
                        onChange={(v) => setAppointmentData({ ...appointmentData, date: v })}
                      />
                      <FontAwesomeIcon icon={faClock} className="patient-details-date-icon" />
                    </div>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Caregiver</label>
                    <select
                      className="patient-details-form-control"
                      value={appointmentData.caregiver}
                      onChange={(e) => setAppointmentData({ ...appointmentData, caregiver: e.target.value })}
                    >
                      <option value="">Select Caregiver</option>
                    </select>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Start Time</label>
                    <div className="patient-details-date-input-wrapper">
                      <input
                        type="time"
                        className="patient-details-form-control patient-details-date-input patient-details-time-input"
                        value={appointmentData.startTime}
                        onChange={(e) => setAppointmentData({ ...appointmentData, startTime: e.target.value })}
                        placeholder="--:--"
                      />
                      <FontAwesomeIcon icon={faClock} className="patient-details-date-icon" />
                    </div>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Visit Type</label>
                    <select
                      className="patient-details-form-control"
                      value={appointmentData.visitType}
                      onChange={(e) => setAppointmentData({ ...appointmentData, visitType: e.target.value })}
                    >
                      <option value="">Select Visit Type</option>
                    </select>
                  </div>
                  <div className="patient-details-form-group">
                    <label>End Time</label>
                    <div className="patient-details-date-input-wrapper">
                      <input
                        type="time"
                        className="patient-details-form-control patient-details-date-input patient-details-time-input"
                        value={appointmentData.endTime}
                        onChange={(e) => setAppointmentData({ ...appointmentData, endTime: e.target.value })}
                        placeholder="--:--"
                      />
                      <FontAwesomeIcon icon={faClock} className="patient-details-date-icon" />
                    </div>
                  </div>
                  <div className="patient-details-form-group">
                    <label>Visit Status</label>
                    <select
                      className="patient-details-form-control"
                      value={appointmentData.visitStatus}
                      onChange={(e) => setAppointmentData({ ...appointmentData, visitStatus: e.target.value })}
                    >
                      <option value="">Select Visit Status</option>
                    </select>
                  </div>
                </div>
                <div className="patient-details-modal-footer">
                  <button
                    type="button"
                    className="patient-details-btn-cancel"
                    onClick={() => {
                      setShowAppointmentModal(false);
                      setAppointmentData({
                        facility: '',
                        date: new Date().toISOString().split('T')[0],
                        startTime: '',
                        endTime: '',
                        provider: '',
                        caregiver: '',
                        visitType: '',
                        visitStatus: ''
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="patient-details-btn-save"
                    onClick={() => {
                      // Handle save appointment
                      setShowAppointmentModal(false);
                    }}
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="patient-details-detail-item">
      <span className="patient-details-detail-label">{label}</span>
      <span className="patient-details-detail-colon">:</span>
      <span className="patient-details-detail-value">{value}</span>
    </div>
  );
}

function VitalCard({ title, date, value, systolic, diastolic, unit, pulse, alertStatus = 'normal', bpStatus, pulseStatus = 'normal', noData, isConfigured = true, onCardClick, onFirstIconClick, onSecondIconClick }) {
  const isAbnormalPulse = pulseStatus === 'high' || pulseStatus === 'low';
  const hasPulse = pulse != null && pulse !== '' && pulse !== '-';
  // Keep actions enabled when either:
  // 1) vital is configured, OR
  // 2) at least one reading exists (noData === false).
  // This prevents false-disabled buttons when config flags are missing but data exists.
  const iconsEnabled = isConfigured || !noData;
  const roundDisplay = (v) => {
    const n = Number(v);
    return (v != null && v !== '' && !Number.isNaN(n)) ? Math.round(n) : v;
  };
  const displaySys = roundDisplay(systolic);
  const displayDia = roundDisplay(diastolic);

  return (
    <div className={`patient-details-vital-card ${onCardClick ? 'patient-details-vital-card-clickable' : ''}`} onClick={onCardClick}>
      <div className="patient-details-vital-header">
        {title}
        {date && <p className="patient-details-vital-date">{date}</p>}
      </div>
      <div className="patient-details-vital-body">
        {noData ? (
          <>
            <p className="patient-details-vital-value-no-data">-</p>
            <p className="patient-details-vital-unit">{unit}</p>
          </>
        ) : (
          <>
            {title === 'Blood Pressure' ? (
              <p className="patient-details-vital-value">
                <span className={bpStatus?.sysStatus === 'high' ? 'alert-high' : bpStatus?.sysStatus === 'low' ? 'alert-low' : ''}>{displaySys}</span>
                <span className="patient-details-vital-separator"> / </span>
                <span className={bpStatus?.diaStatus === 'high' ? 'alert-high' : bpStatus?.diaStatus === 'low' ? 'alert-low' : ''}>{displayDia}</span>
              </p>
            ) : (
              <p
                className={`patient-details-vital-value ${alertStatus === 'high'
                  ? 'alert-high'
                  : alertStatus === 'low'
                    ? 'alert-low'
                    : ''
                  }`}
              >
                {value}
              </p>
            )}
            <p className="patient-details-vital-unit">{unit}</p>
          </>
        )}
        <div className="patient-details-pulse-info">
          Pulse{' '}
          <span
            className={`patient-details-pulse-value${
              pulseStatus === 'high' ? ' alert-high' : pulseStatus === 'low' ? ' alert-low' : ''
            }`}
          >
            {pulse || '-'}
            {hasPulse ? (
              <PulseIcon isAbnormal={isAbnormalPulse} className="patient-details-pulse-icon" />
            ) : null}
          </span>
        </div>
      </div>
      <div className="patient-details-vital-footer">
        {noData && <span className="patient-details-no-data-text">No data</span>}
        <div className="patient-details-vital-icons">
          <button
            className={`patient-details-icon-btn ${!iconsEnabled ? 'patient-details-icon-btn-disabled' : ''}`}
            disabled={!iconsEnabled}
            onClick={(e) => {
              e.stopPropagation();
              if (iconsEnabled && onFirstIconClick) onFirstIconClick();
            }}
            title={!iconsEnabled ? 'Vital not configured' : (noData ? 'Add/view measurements' : 'View measurements')}
          >
            <FontAwesomeIcon icon={faFileText} />
          </button>
          <button
            className={`patient-details-icon-btn ${!iconsEnabled ? 'patient-details-icon-btn-disabled' : ''}`}
            disabled={!iconsEnabled}
            onClick={(e) => {
              e.stopPropagation();
              if (iconsEnabled && onSecondIconClick) onSecondIconClick();
            }}
            title={!iconsEnabled ? 'Vital not configured' : (noData ? 'View chart (no data yet)' : 'View chart')}
          >
            <FontAwesomeIcon icon={faAreaChart} />
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteItem({ author, date, text }) {
  return (
    <div className="patient-details-note-item">
      <div className="patient-details-note-meta">
        <span>{author}</span>
        <span className="patient-details-note-date">{date}</span>
      </div>
      <p>{text}</p>
    </div>
  );
}
